import { devLog, devError } from '../utils/devLogger';
import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Form,
  Alert,
  Spinner,
  Tab,
  Tabs,
  Modal
} from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, where, orderBy, documentId } from 'firebase/firestore';
import qaService from '../services/qaService';
import attendanceService from '../services/attendanceService';
import NotificationService from '../services/notificationService';
import DocumentReviewPanel from '../components/DocumentReviewPanel';
import ProgressReportGenerator from '../components/ProgressReportGenerator';
import QANavbar from '../components/QANavbar';
import QAReportCenter from '../components/QAReportCenter';
import TemplateReviewPanel from '../components/TemplateReviewPanel';
import SubmissionRequestReviewPanel from '../components/SubmissionRequestReviewPanel';
import PreviousSessionResources from '../components/PreviousSessionResources';
import AdminDocumentUpload from '../components/AdminDocumentUpload';
import { DOCUMENT_CATEGORIES, DOCUMENT_CATEGORIES_BY_ID, DOCUMENT_CATEGORIES_BY_NAME } from '../config/documentCategories';
import { downloadProgramDocuments } from '../services/programDocumentDownloadService';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
// ExcelJS is ~1.2 MB — loaded on demand only when the user triggers an export
import '../styles/brand.css';

const QADashboard = () => {
  usePageTitle("QA Dashboard");
  const { currentUser, userProfile, isQA, isAdmin } = useAuth();
  const navigate = useNavigate();

  // State
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [groupedDocuments, setGroupedDocuments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('all-docs');
  const [expandedPrograms, setExpandedPrograms] = useState({});
  const [downloadingProgramId, setDownloadingProgramId] = useState('');
  // cohorts declared here so getCohortName and uniqueCohorts useMemo can close over it
  const [cohorts, setCohorts] = useState([]);

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterValidation, setFilterValidation] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStudent, setFilterStudent] = useState('all');
  const [filterCohort, setFilterCohort] = useState('all');
  const [submissionStatusFilter, setSubmissionStatusFilter] = useState('all');

  const getCohortName = (cohortId, fallbackName = '') => {
    if (!cohortId) return fallbackName || 'Unassigned';
    const matched = cohorts.find((cohort) => cohort.id === cohortId);
    return matched?.name || fallbackName || 'Unassigned';
  };

  const uniqueStudents = useMemo(() => {
    const studentMap = new Map();
    documents.forEach(doc => {
      if (doc.userInfo?.id && doc.userInfo?.displayName) {
        studentMap.set(doc.userInfo.id, {
          id: doc.userInfo.id,
          name: doc.userInfo.displayName,
          email: doc.userInfo.email
        });
      }
    });
    return Array.from(studentMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [documents]);

  const uniqueCohorts = useMemo(() => {
    const cohortMap = new Map();
    documents.forEach((doc) => {
      const cohortId = doc.userInfo?.cohortId || doc.cohortId;
      const cohortName = getCohortName(cohortId, doc.userInfo?.cohortName || doc.cohortName);
      if (cohortId) {
        cohortMap.set(cohortId, { id: cohortId, name: cohortName });
      }
    });
    return Array.from(cohortMap.values()).sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''))
    );
  }, [documents, cohorts]); // eslint-disable-line react-hooks/exhaustive-deps

  const getEffectiveReviewStatus = (doc) => {
    if (doc.reviewStatus) return doc.reviewStatus;
    if (doc.status === 'verified' || doc.status === 'approved') return 'qa_approved';
    if (doc.status === 'rejected') return 'qa_rejected';
    return '';
  };

  const getEffectiveStatus = (doc) => {
    const reviewStatus = getEffectiveReviewStatus(doc);
    if (reviewStatus === 'qa_approved') return 'verified';
    if (reviewStatus === 'qa_rejected') return 'rejected';
    return doc.status || 'pending';
  };

  const isPendingDoc = (doc) => {
    const effectiveStatus = getEffectiveStatus(doc);
    const reviewStatus = getEffectiveReviewStatus(doc);
    return (
      doc.reviewStatus === 'pending_review' ||
      doc.flaggedForReview === true ||
      (!doc.reviewStatus && effectiveStatus === 'pending') ||
      doc.reviewStatus === 'not_reviewed' ||
      (doc.status === 'pending' && !doc.reviewStatus)
    ) && reviewStatus !== 'qa_approved' && reviewStatus !== 'qa_rejected';
  };

  const isInactiveLifecycleStatus = (statusValue) => {
    const normalized = String(statusValue || '')
      .trim()
      .toLowerCase()
      .replace(/[_\s-]+/g, '');
    return [
      'inactive',
      'dropoff',
      'dropped',
      'withdraw',
      'withdrawn',
      'withdrawal',
      'deactivated'
    ].includes(normalized);
  };

  const isActiveLearner = (learner) => {
    if (!learner) return false;
    if (learner.deleted === true) return false;
    if (learner.deactivated === true) return false;
    if (learner.isActive === false) return false;
    if (isInactiveLifecycleStatus(learner.status)) {
      return false;
    }
    return true;
  };

  const resolveDocumentCategoryKey = (doc) => {
    const candidates = [
      doc?.categoryKey,
      doc?.categoryId,
      doc?.category,
      doc?.type,
      doc?.documentType
    ].filter(Boolean);

    for (const candidate of candidates) {
      if (DOCUMENT_CATEGORIES[candidate]) return candidate;
      if (DOCUMENT_CATEGORIES_BY_ID[candidate]) return DOCUMENT_CATEGORIES_BY_ID[candidate].key;
      if (DOCUMENT_CATEGORIES_BY_NAME[candidate]) return DOCUMENT_CATEGORIES_BY_NAME[candidate].key;

      const upperCandidate = String(candidate).toUpperCase();
      if (DOCUMENT_CATEGORIES[upperCandidate]) return upperCandidate;

      const normalizedLegacy = String(candidate)
        .trim()
        .replace(/\s+/g, '_')
        .replace(/-/g, '_')
        .toUpperCase();
      if (DOCUMENT_CATEGORIES[normalizedLegacy]) return normalizedLegacy;
    }

    return null;
  };

  // Selected document for review
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showReviewPanel, setShowReviewPanel] = useState(false);

  // Statistics
  const [stats, setStats] = useState(null);
  const [turnaroundStats, setTurnaroundStats] = useState(null);
  const [rejectionStats, setRejectionStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Date range for reports
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Learner registration status
  const [learners, setLearners] = useState([]);
  const [loadingLearners, setLoadingLearners] = useState(false);
  const [learnerFilter, setLearnerFilter] = useState('all'); // all, registered, unregistered, logged_in, never_logged_in
  const [learnerProgramFilter, setLearnerProgramFilter] = useState('all');
  const [selectedCohortIds, setSelectedCohortIds] = useState([]);
  const [learnerSearchTerm, setLearnerSearchTerm] = useState('');
  const [showLearnerSubmissionModal, setShowLearnerSubmissionModal] = useState(false);
  const [selectedLearnerSubmission, setSelectedLearnerSubmission] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [modules, setModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [attendanceFacilitators, setAttendanceFacilitators] = useState([]);
  const [selectedAttendanceFacilitatorId, setSelectedAttendanceFacilitatorId] = useState('');
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [sessionAttendance, setSessionAttendance] = useState([]);
  const [attendanceMarkerMap, setAttendanceMarkerMap] = useState({});
  const [attendanceFacilitatorFilter, setAttendanceFacilitatorFilter] = useState('');
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingSessionAttendance, setLoadingSessionAttendance] = useState(false);
  const [sessionAttendanceError, setSessionAttendanceError] = useState('');
  const [historicCohortId, setHistoricCohortId] = useState('');
  const [historicModuleId, setHistoricModuleId] = useState('');
  const [historicSessionDate, setHistoricSessionDate] = useState('');
  const [historicSessionTime, setHistoricSessionTime] = useState('');
  const [historicSessionTitle, setHistoricSessionTitle] = useState('');
  const [historicSessionNotes, setHistoricSessionNotes] = useState('');
  const [historicRequestConfirmation, setHistoricRequestConfirmation] = useState(false);
  const [historicAttendanceDraft, setHistoricAttendanceDraft] = useState({});
  const [savingHistoricSession, setSavingHistoricSession] = useState(false);
  const [historicSessionSuccess, setHistoricSessionSuccess] = useState('');
  const [confirmationSessions, setConfirmationSessions] = useState([]);
  const [loadingConfirmations, setLoadingConfirmations] = useState(false);
  const [confirmationError, setConfirmationError] = useState('');
  const [teamsCohortId, setTeamsCohortId] = useState('');
  const [teamsCsvRows, setTeamsCsvRows] = useState([]);
  const [teamsCheckResults, setTeamsCheckResults] = useState(null);
  const [teamsCheckError, setTeamsCheckError] = useState('');
  const [teamsCheckLoading, setTeamsCheckLoading] = useState(false);
  const [teamsSessionDate, setTeamsSessionDate] = useState(new Date().toISOString().split('T')[0]);
  const [teamsSessionTime, setTeamsSessionTime] = useState('09:00');
  const [creatingTeamsSession, setCreatingTeamsSession] = useState(false);
  const [teamsSessionSuccess, setTeamsSessionSuccess] = useState('');
  const [customSessionTitle, setCustomSessionTitle] = useState('');
  const [customSessionDate, setCustomSessionDate] = useState('');
  const [customSessionTime, setCustomSessionTime] = useState('');
  const [customSessionExpectedEmails, setCustomSessionExpectedEmails] = useState('');
  const [customTeamsCsvRows, setCustomTeamsCsvRows] = useState([]);
  const [customCheckResults, setCustomCheckResults] = useState(null);
  const [customCheckError, setCustomCheckError] = useState('');
  const [customCheckLoading, setCustomCheckLoading] = useState(false);
  const [creatingCustomSession, setCreatingCustomSession] = useState(false);
  const [customSessionSuccess, setCustomSessionSuccess] = useState('');

  useEffect(() => {
    // Check if user has QA or admin role
    if (!isQA() && !isAdmin()) {
      navigate('/');
      return;
    }

    loadDocuments();
  }, [currentUser, userProfile]);

  useEffect(() => {
    applyFilters();
  }, [documents, filterStatus, filterValidation, searchTerm, activeTab, filterStudent, filterCohort]);

  useEffect(() => {
    if (sessions.length > 0 && learners.length > 0) {
      loadPendingConfirmations();
    }
  }, [sessions, learners]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError('');

      devLog('QA Dashboard: Loading documents...');

      // Load documents grouped by program
      const grouped = await qaService.getDocumentsGroupedByProgram();
      devLog('QA Dashboard: Loaded grouped documents:', grouped);
      setGroupedDocuments(grouped);

      // Also load flat list for other tabs
      const allDocs = grouped.grouped ?
        Object.values(grouped.grouped).flatMap(g => g.documents) :
        [];
      devLog('QA Dashboard: Total documents:', allDocs.length);
      setDocuments(allDocs);

      // Expand all programs by default
      if (grouped.grouped) {
        const expanded = {};
        Object.keys(grouped.grouped).forEach(programId => {
          expanded[programId] = true;
        });
        setExpandedPrograms(expanded);
      }
    } catch (err) {
      devError('Error loading documents:', err);
      setError('Failed to load documents: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load learners with registration status
  const loadLearners = async () => {
    try {
      setLoadingLearners(true);

      // Load all learners
      const learnersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'learner')
      );
      const learnersSnapshot = await getDocs(learnersQuery);
      const learnersList = learnersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || null
      }));
      setLearners(learnersList);

      // Load all programs
      const programsSnapshot = await getDocs(collection(db, 'programs'));
      const programsList = programsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPrograms(programsList);

      // Load all cohorts
      const cohortsSnapshot = await getDocs(collection(db, 'cohorts'));
      const cohortsList = cohortsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCohorts(cohortsList);

      devLog(`Loaded ${learnersList.length} learners, ${programsList.length} programs, and ${cohortsList.length} cohorts`);
    } catch (err) {
      devError('Error loading learners:', err);
      setError('Failed to load learners: ' + err.message);
    } finally {
      setLoadingLearners(false);
    }
  };

  const loadModules = async () => {
    try {
      setLoadingModules(true);
      const modulesSnapshot = await getDocs(collection(db, 'modules'));
      const modulesList = modulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setModules(modulesList);
    } catch (err) {
      devError('Error loading modules:', err);
      setSessionAttendanceError('Failed to load modules: ' + err.message);
    } finally {
      setLoadingModules(false);
    }
  };

  const loadSessions = async () => {
    try {
      setLoadingSessions(true);
      setSessionAttendanceError('');

      const sessionsSnapshot = await getDocs(
        query(collection(db, 'sessions'), orderBy('scheduledDate', 'desc'))
      );
      const sessionsList = sessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        scheduledDate: doc.data().scheduledDate?.toDate?.() || null
      }));
      setSessions(sessionsList);

      const facilitatorIds = Array.from(
        new Set(
          sessionsList
            .map((session) => session.attendanceMarkedBy || session.createdBy || session.facilitatorId)
            .filter(Boolean)
        )
      );
      if (facilitatorIds.length) {
        const facilitatorMap = {};
        for (let i = 0; i < facilitatorIds.length; i += 10) {
          const chunk = facilitatorIds.slice(i, i + 10);
          const snapshot = await getDocs(
            query(collection(db, 'users'), where(documentId(), 'in', chunk))
          );
          snapshot.docs.forEach((docSnap) => {
            const data = docSnap.data();
            facilitatorMap[docSnap.id] = {
              id: docSnap.id,
              name: data.displayName || data.name || data.email || 'Facilitator',
              email: data.email || ''
            };
          });
        }
        setAttendanceFacilitators(Object.values(facilitatorMap));
      } else {
        setAttendanceFacilitators([]);
      }
    } catch (err) {
      devError('Error loading sessions:', err);
      setSessionAttendanceError('Failed to load sessions: ' + err.message);
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadPendingConfirmations = async () => {
    try {
      if (!sessions.length || !learners.length) {
        setConfirmationSessions([]);
        return;
      }
      setLoadingConfirmations(true);
      setConfirmationError('');

      const now = new Date();
      const learnersByEmail = new Map(
        learners.map((learner) => [String(learner.email || '').toLowerCase(), learner])
      );

      const confirmationCandidates = sessions.filter((session) => {
        if (!session.requiresAttendanceConfirmation) return false;
        const scheduledDate = session.scheduledDate instanceof Date ? session.scheduledDate : null;
        return !scheduledDate || scheduledDate <= now;
      });

      const results = [];
      for (const session of confirmationCandidates) {
        const sessionLearners = learners.filter((learner) => learner.cohortId === session.cohortId);
        const assignedUsers = Array.isArray(session.assignedUserIds) ? session.assignedUserIds : [];
        const assignedEmails = Array.isArray(session.assignedUserEmails) ? session.assignedUserEmails : [];
        const assignedLearners = assignedEmails
          .map((email) => learnersByEmail.get(String(email).toLowerCase()))
          .filter(Boolean);

        const expectedLearnerIds = new Set([
          ...sessionLearners.map((learner) => learner.id),
          ...assignedUsers,
          ...assignedLearners.map((learner) => learner.id)
        ]);

        if (expectedLearnerIds.size === 0) {
          continue;
        }

        const attendanceResult = await attendanceService.getSessionAttendance(session.id);
        if (!attendanceResult.success) {
          continue;
        }

        const confirmedCount = attendanceResult.attendance.filter(
          (record) => record.source === 'learner-self-marked'
        ).length;

        const pendingCount = Math.max(expectedLearnerIds.size - confirmedCount, 0);
        if (pendingCount === 0) {
          continue;
        }

        results.push({
          id: session.id,
          title: session.title || session.moduleName || 'Session',
          cohortName: session.cohortName || cohorts.find(c => c.id === session.cohortId)?.name || 'Cohort',
          scheduledDate: session.scheduledDate?.toLocaleDateString?.() || 'Unknown',
          expectedCount: expectedLearnerIds.size,
          confirmedCount,
          pendingCount
        });
      }

      setConfirmationSessions(results);
    } catch (err) {
      devError('Error loading confirmations:', err);
      setConfirmationError('Failed to load attendance confirmations.');
    } finally {
      setLoadingConfirmations(false);
    }
  };

  const loadSessionAttendance = async () => {
    if (!selectedSessionId) {
      setSessionAttendanceError('Please select a session.');
      return;
    }

    try {
      setLoadingSessionAttendance(true);
      setSessionAttendanceError('');
      const result = await attendanceService.getSessionAttendance(selectedSessionId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to load session attendance.');
      }
      const records = result.attendance || [];
      setSessionAttendance(records);

      const selectedSession = sessions.find((item) => item.id === selectedSessionId);
      const fallbackMarkerId =
        selectedSession?.attendanceMarkedBy ||
        selectedSession?.createdBy ||
        selectedSession?.facilitatorId ||
        null;
      const markerIds = Array.from(
        new Set(
          records
            .map((record) => record.markedBy || fallbackMarkerId)
            .filter(Boolean)
        )
      );
      if (!markerIds.length) {
        setAttendanceMarkerMap({});
      } else {
        const markerMap = {};
        for (let i = 0; i < markerIds.length; i += 10) {
          const chunk = markerIds.slice(i, i + 10);
          const snapshot = await getDocs(
            query(collection(db, 'users'), where(documentId(), 'in', chunk))
          );
          snapshot.docs.forEach((docSnap) => {
            const data = docSnap.data();
            markerMap[docSnap.id] = {
              id: docSnap.id,
              name: data.displayName || data.name || data.email || 'Facilitator',
              email: data.email || ''
            };
          });
        }
        setAttendanceMarkerMap(markerMap);
      }
    } catch (err) {
      devError('Error loading session attendance:', err);
      setSessionAttendanceError('Failed to load attendance: ' + err.message);
    } finally {
      setLoadingSessionAttendance(false);
    }
  };

  const getSelectedAttendanceSession = () => (
    sessions.find((session) => session.id === selectedSessionId) || null
  );

  const getFilteredAttendanceRecords = () => {
    return filteredSessionAttendance || [];
  };

  const downloadAttendanceCsv = () => {
    const session = getSelectedAttendanceSession();
    const records = getFilteredAttendanceRecords();
    if (!session || records.length === 0) return;
    const header = ['Learner', 'Email', 'Status', 'Marked At', 'Source', 'Marked By'];
    const rows = records.map((record) => {
      const learner =
        learners.find(l => l.uid === record.learnerId) ||
        learners.find(l => l.id === record.learnerId);
      const markerId = getEffectiveMarkerId(record);
      const marker = attendanceMarkerMap[markerId];
      const markedAt = record.markedAt?.toDate
        ? record.markedAt.toDate().toLocaleString()
        : (record.markedAt ? new Date(record.markedAt).toLocaleString() : 'N/A');
      return [
        learner?.displayName || 'Unknown',
        learner?.email || 'N/A',
        record.status || 'unknown',
        markedAt,
        record.source || 'manual',
        marker?.name || markerId || 'N/A'
      ];
    });
    const content = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateLabel = session.scheduledDate ? session.scheduledDate.toLocaleDateString() : 'session';
    link.download = `attendance_${session.cohortName || session.cohortId || 'session'}_${dateLabel}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadAttendancePdf = () => {
    const session = getSelectedAttendanceSession();
    const records = getFilteredAttendanceRecords();
    if (!session || records.length === 0) return;
    const doc = new jsPDF();
    const dateLabel = session.scheduledDate ? session.scheduledDate.toLocaleDateString() : 'Unknown date';
    const sessionTitle = session.title || session.moduleName || 'Session';
    doc.setFontSize(14);
    doc.text('Session Attendance Report', 14, 18);
    doc.setFontSize(11);
    doc.text(`Session: ${sessionTitle}`, 14, 26);
    doc.text(`Cohort: ${session.cohortName || 'Unknown Cohort'}`, 14, 32);
    doc.text(`Date: ${dateLabel}`, 14, 38);

    const rows = records.map((record) => {
      const learner =
        learners.find(l => l.uid === record.learnerId) ||
        learners.find(l => l.id === record.learnerId);
      const markerId = getEffectiveMarkerId(record);
      const marker = attendanceMarkerMap[markerId];
      const markedAt = record.markedAt?.toDate
        ? record.markedAt.toDate().toLocaleString()
        : (record.markedAt ? new Date(record.markedAt).toLocaleString() : 'N/A');
      return [
        learner?.displayName || 'Unknown',
        learner?.email || 'N/A',
        record.status || 'unknown',
        markedAt,
        record.source || 'manual',
        marker?.name || markerId || 'N/A'
      ];
    });

    autoTable(doc, {
      head: [['Learner', 'Email', 'Status', 'Marked At', 'Source', 'Marked By']],
      body: rows,
      startY: 44,
      styles: { fontSize: 9 }
    });

    doc.save(`attendance_${session.cohortName || session.cohortId || 'session'}_${dateLabel}.pdf`);
  };

  const getHistoricLearners = () => {
    if (!historicCohortId) {
      return [];
    }
    return learners.filter((learner) => learner.cohortId === historicCohortId);
  };

  const updateHistoricAttendance = (learnerId, status) => {
    setHistoricAttendanceDraft((prev) => ({
      ...prev,
      [learnerId]: status
    }));
  };

  const handleCreateHistoricSession = async () => {
    if (!historicCohortId) {
      setSessionAttendanceError('Please select a cohort for the historic session.');
      return;
    }
    if (!historicSessionDate) {
      setSessionAttendanceError('Please select a session date.');
      return;
    }
    if (!historicSessionTime) {
      setSessionAttendanceError('Please select a session time.');
      return;
    }

    const scheduledDate = new Date(`${historicSessionDate}T${historicSessionTime}`);
    if (Number.isNaN(scheduledDate.getTime())) {
      setSessionAttendanceError('Invalid session date or time.');
      return;
    }

    const cohortName = cohorts.find(c => c.id === historicCohortId)?.name || 'Cohort';
    const moduleInfo = modules.find(m => m.id === historicModuleId);
    const moduleName = moduleInfo?.moduleName || moduleInfo?.title || 'Session';
    const sessionTitle = historicSessionTitle || `${moduleName} - ${scheduledDate.toLocaleDateString()}`;
    const sessionLearners = getHistoricLearners();

    if (sessionLearners.length === 0) {
      setSessionAttendanceError('No learners found in the selected cohort.');
      return;
    }

    try {
      setSavingHistoricSession(true);
      setSessionAttendanceError('');
      setHistoricSessionSuccess('');

      const sessionResult = await attendanceService.createSession({
        cohortId: historicCohortId,
        moduleId: historicModuleId || null,
        moduleName: moduleName,
        scheduledDate,
        facilitatorId: currentUser?.uid || null,
        createdBy: currentUser?.uid || 'qa',
        title: sessionTitle,
        notes: historicSessionNotes?.trim() || '',
        cohortName,
        status: 'completed',
        requiresAttendanceConfirmation: historicRequestConfirmation,
        confirmationRequested: false
      });

      if (!sessionResult.success) {
        throw new Error(sessionResult.error || 'Failed to create session.');
      }

      if (historicRequestConfirmation) {
        setHistoricSessionSuccess('Historic session created. Learners will be asked to confirm attendance on login.');
        setSelectedSessionId(sessionResult.sessionId);
        setHistoricAttendanceDraft({});
        await loadSessions();
        await loadPendingConfirmations();
        return;
      }

      const attendancePayload = sessionLearners.map((learner) => ({
        learnerId: learner.id || learner.uid,
        status: historicAttendanceDraft[learner.id] || 'present',
        cohortId: learner.cohortId || historicCohortId,
        moduleId: historicModuleId || null,
        date: scheduledDate,
        source: 'qa-manual'
      }));

      const markResult = await attendanceService.markAttendance(
        sessionResult.sessionId,
        attendancePayload,
        currentUser?.uid || 'qa'
      );

      if (!markResult.success) {
        throw new Error(markResult.error || 'Failed to record attendance.');
      }

      attendancePayload.forEach((record) => {
        const learner = sessionLearners.find((item) => item.id === record.learnerId || item.uid === record.learnerId);
        if (learner) {
          NotificationService.notifyAttendanceConfirmation(learner, sessionTitle);
        }
      });

      setHistoricSessionSuccess('Historic session created and attendance recorded.');
      setSelectedSessionId(sessionResult.sessionId);
      setHistoricAttendanceDraft({});
      setHistoricSessionNotes('');
      await loadSessions();
      await loadSessionAttendance();
      await loadPendingConfirmations();
    } catch (err) {
      devError('Error creating historic session:', err);
      setSessionAttendanceError('Failed to create historic session: ' + err.message);
    } finally {
      setSavingHistoricSession(false);
    }
  };

  const getLearnerProgramIds = (learner) => {
    const ids = [];
    if (learner?.programId) ids.push(learner.programId);

    const learnerCohortIds = [
      learner?.cohortId,
      ...(Array.isArray(learner?.cohortIds) ? learner.cohortIds : []),
      ...(Array.isArray(learner?.assignedCohorts) ? learner.assignedCohorts : [])
    ].filter(Boolean);

    learnerCohortIds.forEach((cohortId) => {
      const cohortProgramId = cohorts.find((c) => c.id === cohortId)?.programId;
      if (cohortProgramId) ids.push(cohortProgramId);
    });

    return Array.from(new Set(ids));
  };

  const getLearnerProgramId = (learner) => (
    getLearnerProgramIds(learner)[0] || null
  );

  const learnerMatchesProgram = (learner, programId) => {
    if (programId === 'all') return true;
    return getLearnerProgramIds(learner).includes(programId);
  };

  const getAvailableCohorts = () => (
    cohorts.filter(c => learnerProgramFilter === 'all' || c.programId === learnerProgramFilter)
  );

  const getSelectedCohortNames = () => (
    selectedCohortIds
      .map((id) => cohorts.find(c => c.id === id)?.name)
      .filter(Boolean)
  );

  const getSelectedCohortLabel = () => {
    if (!selectedCohortIds.length) return 'All Cohorts';
    const names = getSelectedCohortNames();
    if (!names.length) return 'Selected Cohorts';
    if (names.length <= 3) return names.join(', ');
    return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
  };

  const getSelectedCohortSuffix = () => {
    if (!selectedCohortIds.length) return '';
    if (selectedCohortIds.length === 1) {
      const name = getSelectedCohortNames()[0] || 'cohort';
      return `_${name.replace(/\s+/g, '_')}`;
    }
    return `_${selectedCohortIds.length}_cohorts`;
  };

  const handleProgramFilterChange = (value) => {
    setLearnerProgramFilter(value);
    if (value === 'all') {
      return;
    }
    const allowed = new Set(cohorts.filter(c => c.programId === value).map(c => c.id));
    setSelectedCohortIds((prev) => prev.filter(id => allowed.has(id)));
  };

  const cohortMatchesSelection = (cohortId) => {
    if (!selectedCohortIds.length) return true;
    return selectedCohortIds.includes(cohortId);
  };

  const getLearnerCohortIds = (learner) => {
    const ids = [];
    if (learner?.cohortId) ids.push(learner.cohortId);
    if (Array.isArray(learner?.cohortIds)) ids.push(...learner.cohortIds.filter(Boolean));
    if (Array.isArray(learner?.assignedCohorts)) ids.push(...learner.assignedCohorts.filter(Boolean));
    return Array.from(new Set(ids));
  };

  const toggleCohortSelection = (cohortId) => {
    setSelectedCohortIds((prev) => (
      prev.includes(cohortId)
        ? prev.filter(id => id !== cohortId)
        : [...prev, cohortId]
    ));
  };

  const selectAllCohorts = () => {
    setSelectedCohortIds(getAvailableCohorts().map(c => c.id));
  };

  const clearCohortSelection = () => {
    setSelectedCohortIds([]);
  };

  const getBaseFilteredLearners = () => {
    let filtered = learners.filter((learner) => isActiveLearner(learner));
    if (learnerProgramFilter !== 'all') {
      filtered = filtered.filter(l => learnerMatchesProgram(l, learnerProgramFilter));
    }
    if (selectedCohortIds.length) {
      filtered = filtered.filter((learner) => {
        const cohortIds = getLearnerCohortIds(learner);
        if (!cohortIds.length) return cohortMatchesSelection(learner.cohortId);
        return cohortIds.some((id) => selectedCohortIds.includes(id));
      });
    }
    return filtered;
  };

  const getScopedLearnersIncludingInactive = () => {
    let filtered = [...learners];
    if (learnerProgramFilter !== 'all') {
      filtered = filtered.filter((l) => learnerMatchesProgram(l, learnerProgramFilter));
    }
    if (selectedCohortIds.length) {
      filtered = filtered.filter((learner) => {
        const cohortIds = getLearnerCohortIds(learner);
        if (!cohortIds.length) return cohortMatchesSelection(learner.cohortId);
        return cohortIds.some((id) => selectedCohortIds.includes(id));
      });
    }
    return filtered;
  };

  // Get filtered learners based on filters
  const getFilteredLearners = () => {
    let filtered = getBaseFilteredLearners();

    // Filter by registration/login status
    if (learnerFilter === 'registered') {
      filtered = filtered.filter(l => l.lastLoginAt);
    } else if (learnerFilter === 'unregistered') {
      filtered = filtered.filter(l => !l.lastLoginAt);
    } else if (learnerFilter === 'logged_in') {
      filtered = filtered.filter(l => l.lastLoginAt);
    } else if (learnerFilter === 'never_logged_in') {
      filtered = filtered.filter(l => !l.lastLoginAt);
    }

    // Search filter
    if (learnerSearchTerm) {
      const term = learnerSearchTerm.toLowerCase();
      filtered = filtered.filter(l =>
        l.displayName?.toLowerCase().includes(term) ||
        l.email?.toLowerCase().includes(term) ||
        l.idNumber?.includes(term)
      );
    }

    if (submissionStatusFilter === 'submitted') {
      filtered = filtered.filter((learner) => getLearnerSubmissionStats(learner).submittedCount > 0);
    } else if (submissionStatusFilter === 'not_submitted') {
      filtered = filtered.filter((learner) => getLearnerSubmissionStats(learner).submittedCount === 0);
    }

    return filtered;
  };

  // Get learner counts for badges
  const getLearnerCounts = () => {
    const activeBase = getBaseFilteredLearners();
    const scopedAll = getScopedLearnersIncludingInactive();
    const registered = activeBase.filter(l => l.lastLoginAt).length;
    const unregistered = activeBase.filter(l => !l.lastLoginAt).length;
    const loggedIn = activeBase.filter(l => l.lastLoginAt).length;
    const neverLoggedIn = activeBase.filter(l => !l.lastLoginAt).length;
    return { registered, unregistered, loggedIn, neverLoggedIn, total: scopedAll.length, activeTotal: activeBase.length };
  };

  // Get login counts for current cohort filter
  const getCohortLoginCounts = () => {
    const activeFiltered = getBaseFilteredLearners();
    const scopedAll = getScopedLearnersIncludingInactive();
    const loggedIn = activeFiltered.filter(l => l.lastLoginAt).length;
    const neverLoggedIn = activeFiltered.filter(l => !l.lastLoginAt).length;
    return { loggedIn, neverLoggedIn, total: scopedAll.length, activeTotal: activeFiltered.length };
  };

  const getFilteredSessions = () => (
    sessions.filter((session) => {
      const cohort = cohorts.find(c => c.id === session.cohortId);
      const programId = session.programId || cohort?.programId || null;
      if (learnerProgramFilter !== 'all' && programId !== learnerProgramFilter) {
        return false;
      }
      if (selectedCohortIds.length && !selectedCohortIds.includes(session.cohortId)) {
        return false;
      }
      return true;
    })
  );

  const getRequiredDocumentCategories = () => (
    Object.keys(DOCUMENT_CATEGORIES).filter((key) => DOCUMENT_CATEGORIES[key]?.mandatory)
  );

  const getLearnerSubmissionStats = (learner) => {
    const requiredCategories = getRequiredDocumentCategories();
    const learnerId = learner?.id || learner?.uid;
    if (!learnerId) {
      return { submittedCount: 0, outstandingCount: requiredCategories.length, submittedCategories: [], outstandingCategories: requiredCategories };
    }

    const learnerDocs = documents.filter((doc) => {
      const docLearnerId = doc.userInfo?.id || doc.userId || doc.learnerId;
      return docLearnerId === learnerId;
    });

    const submittedCategoriesSet = new Set();
    learnerDocs.forEach((doc) => {
      const categoryKey = resolveDocumentCategoryKey(doc);
      if (categoryKey && requiredCategories.includes(categoryKey)) {
        submittedCategoriesSet.add(categoryKey);
      }
    });

    const submittedCategories = Array.from(submittedCategoriesSet);
    const outstandingCategories = requiredCategories.filter((categoryKey) => !submittedCategoriesSet.has(categoryKey));

    return {
      submittedCount: submittedCategories.length,
      outstandingCount: outstandingCategories.length,
      submittedCategories,
      outstandingCategories
    };
  };

  const openLearnerSubmissionBreakdown = (learner) => {
    const stats = getLearnerSubmissionStats(learner);
    setSelectedLearnerSubmission({
      learner,
      ...stats
    });
    setShowLearnerSubmissionModal(true);
  };

  useEffect(() => {
    if (!selectedSessionId) return;
    const availableSessions = new Set(getFilteredSessions().map(session => session.id));
    if (!availableSessions.has(selectedSessionId)) {
      setSelectedSessionId('');
      setSessionAttendance([]);
    }
  }, [selectedSessionId, sessions, cohorts, learnerProgramFilter, selectedCohortIds]);

  // Export activation report to CSV
  const exportActivationReport = () => {
    const filteredLearners = getFilteredLearners();
    const headers = ['Name', 'Email', 'ID Number', 'Program', 'Cohort', 'Registration Status', 'Login Status', 'Last Login', 'Created Date'];

    const csvRows = [
      headers.join(','),
      ...filteredLearners.map(learner => {
        const programName = programs.find(p => p.id === getLearnerProgramId(learner))?.name || 'Unassigned';
        const cohortName = cohorts.find(c => c.id === learner.cohortId)?.name || 'Unassigned';
        const registrationStatus = learner.lastLoginAt ? 'Registered' : 'Unregistered';
        const loginStatus = learner.lastLoginAt ? 'Logged In' : 'Never Logged In';
        const lastLogin = learner.lastLoginAt?.toDate ? learner.lastLoginAt.toDate().toLocaleString() : (learner.lastLoginAt ? new Date(learner.lastLoginAt).toLocaleString() : 'N/A');
        const createdDate = learner.createdAt ? learner.createdAt.toLocaleDateString() : 'N/A';

        return [
          `"${(learner.displayName || 'No Name').replace(/"/g, '""')}"`,
          `"${learner.email || ''}"`,
          `"${learner.idNumber || 'N/A'}"`,
          `"${programName.replace(/"/g, '""')}"`,
          `"${cohortName.replace(/"/g, '""')}"`,
          registrationStatus,
          loginStatus,
          `"${lastLogin}"`,
          createdDate
        ].join(',');
      })
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `login_report${getSelectedCohortSuffix()}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportMasterSheet = () => {
    const filteredLearners = getBaseFilteredLearners();
    const headers = [
      'Name',
      'First Name',
      'Last Name',
      'Email',
      'Phone',
      'Alternate Phone',
      'ID Number',
      'Gender',
      'Race',
      'Date of Birth',
      'Nationality',
      'Program',
      'Cohort',
      'Address Line',
      'City',
      'Province',
      'Postal Code',
      'Registered',
      'Verified',
      'Last Login',
      'Created Date'
    ];

    const rows = [
      headers.join(','),
      ...filteredLearners.map((learner) => {
        const programName = programs.find(p => p.id === getLearnerProgramId(learner))?.name || 'Unassigned';
        const cohortName = cohorts.find(c => c.id === learner.cohortId)?.name || 'Unassigned';
        const lastLogin = learner.lastLoginAt?.toDate
          ? learner.lastLoginAt.toDate().toLocaleString()
          : (learner.lastLoginAt ? new Date(learner.lastLoginAt).toLocaleString() : 'N/A');
        const createdDate = learner.createdAt?.toDate
          ? learner.createdAt.toDate().toLocaleDateString()
          : (learner.createdAt ? new Date(learner.createdAt).toLocaleDateString() : 'N/A');

        const values = [
          learner.displayName || '',
          learner.firstName || '',
          learner.lastName || learner.surname || '',
          learner.email || '',
          learner.phoneNumber || '',
          learner.alternatePhone || learner.alternateNumber || '',
          learner.idNumber || '',
          learner.gender || '',
          learner.race || '',
          learner.dateOfBirth || learner.dob || '',
          learner.nationality || '',
          programName,
          cohortName,
          learner.streetAddress || learner.address || '',
          learner.city || '',
          learner.province || '',
          learner.postalCode || '',
          learner.emailVerified ? 'Yes' : 'No',
          learner.isVerified ? 'Yes' : 'No',
          lastLogin,
          createdDate
        ];

        return values.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',');
      })
    ];

    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `master_sheet_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportCohortSubmissionSpreadsheet = async () => {
    const scopedLearners = getBaseFilteredLearners();
    if (!scopedLearners.length) {
      setError('No learners found for the selected cohort/program scope.');
      return;
    }

    const requiredCategories = getRequiredDocumentCategories();
    const requiredCategorySet = new Set(requiredCategories);
    const learnerIds = new Set(scopedLearners.map((learner) => learner.id || learner.uid).filter(Boolean));

    const docsByLearner = {};
    documents.forEach((docItem) => {
      const learnerId = docItem.userInfo?.id || docItem.userId || docItem.learnerId;
      if (!learnerId || !learnerIds.has(learnerId)) return;
      if (!docsByLearner[learnerId]) docsByLearner[learnerId] = [];
      docsByLearner[learnerId].push(docItem);
    });

    const spreadsheetDocColumns = [
      {
        key: 'eea1',
        label: 'EEA1',
        matches: (docItem) => {
          const text = `${docItem?.category || ''} ${docItem?.categoryId || ''} ${docItem?.categoryKey || ''}`.toLowerCase();
          const fileText = `${docItem?.fileName || ''} ${docItem?.originalFileName || ''}`.toLowerCase();
          return text.includes('eea1') || fileText.includes('eea1');
        }
      },
      {
        key: 'id_copy',
        label: 'ID Copy',
        matches: (docItem) => `${docItem?.category || ''} ${docItem?.categoryId || ''} ${docItem?.categoryKey || ''}`.toLowerCase().includes('id copy')
      },
      {
        key: 'matric_certificate',
        label: 'Matric Certificate',
        matches: (docItem) => {
          const text = `${docItem?.category || ''} ${docItem?.categoryId || ''} ${docItem?.categoryKey || ''}`.toLowerCase();
          return text.includes('matric');
        }
      },
      {
        key: 'highest_qualification',
        label: 'Highest Qualification',
        matches: (docItem) => `${docItem?.category || ''} ${docItem?.categoryId || ''} ${docItem?.categoryKey || ''}`.toLowerCase().includes('highest qualification')
      },
      {
        key: 'police_clearance',
        label: 'Police Clearance',
        matches: (docItem) => {
          const text = `${docItem?.category || ''} ${docItem?.categoryId || ''} ${docItem?.categoryKey || ''}`.toLowerCase();
          return text.includes('criminal') || text.includes('police clearance');
        }
      },
      {
        key: 'unemployment',
        label: 'Unemployment',
        matches: (docItem) => {
          const text = `${docItem?.category || ''} ${docItem?.categoryId || ''} ${docItem?.categoryKey || ''}`.toLowerCase();
          return text.includes('unemployment') || text.includes('affidavit');
        }
      },
      {
        key: 'disability_certification',
        label: 'Disability Certification',
        matches: (docItem) => `${docItem?.category || ''} ${docItem?.categoryId || ''} ${docItem?.categoryKey || ''}`.toLowerCase().includes('disability')
      },
      {
        key: 'cv',
        label: 'CV',
        matches: (docItem) => {
          const text = `${docItem?.category || ''} ${docItem?.categoryId || ''} ${docItem?.categoryKey || ''}`.toLowerCase();
          const fileText = `${docItem?.fileName || ''} ${docItem?.originalFileName || ''}`.toLowerCase();
          return text.includes('cv') || fileText.includes('curriculum vitae') || /\bcv\b/.test(fileText);
        }
      },
      {
        key: 'media_release',
        label: 'Media Release',
        matches: (docItem) => {
          const text = `${docItem?.category || ''} ${docItem?.categoryId || ''} ${docItem?.categoryKey || ''}`.toLowerCase();
          const fileText = `${docItem?.fileName || ''} ${docItem?.originalFileName || ''}`.toLowerCase();
          return (
            text.includes('media release') ||
            text.includes('media-release-form-available-for-online-submission-or-hard-copy') ||
            (text.includes('media') && text.includes('release')) ||
            (fileText.includes('media') && fileText.includes('release'))
          );
        }
      },
      {
        key: 'training_agreement',
        label: 'Training Agreement',
        matches: (docItem) => {
          const text = `${docItem?.category || ''} ${docItem?.categoryId || ''} ${docItem?.categoryKey || ''}`.toLowerCase();
          const fileText = `${docItem?.fileName || ''} ${docItem?.originalFileName || ''}`.toLowerCase();
          return (
            text.includes('training agreement') ||
            text.includes('training-agreement-online-or-hard-copy-available') ||
            (text.includes('training') && text.includes('agreement')) ||
            (fileText.includes('training') && fileText.includes('agreement'))
          );
        }
      }
    ];

    const getActivityStatusLabel = (learner) => {
      if (learner?.deleted || learner?.deactivated) return 'inactive';
      if (learner?.isActive === false) return 'inactive';
      if (isInactiveLifecycleStatus(learner?.status)) return 'inactive';
      return 'active';
    };

    const headers = [
      'Names',
      'ID number',
      'Cohort',
      'Gender',
      'Activity Status',
      ...spreadsheetDocColumns.map((col) => col.label)
    ];

    const categorySummary = spreadsheetDocColumns.reduce((acc, col) => {
      acc[col.key] = { submitted: 0, total: 0 };
      return acc;
    }, {});

    const getCategoryCellValue = (docItem) => {
      if (!docItem) return 'Missing';
      const reviewStatus = getEffectiveReviewStatus(docItem);
      const effectiveStatus = getEffectiveStatus(docItem);
      if (reviewStatus === 'qa_approved' || effectiveStatus === 'verified') return 'Yes';
      if (reviewStatus === 'qa_rejected' || effectiveStatus === 'rejected') return 'Rejected';
      if (String(docItem.validationStatus || '').toLowerCase() === 'invalid') return 'Error';
      if (String(docItem.validationStatus || '').toLowerCase() === 'warning') return 'Warning';
      return 'Pending';
    };

    const dataRows = scopedLearners.map((learner) => {
        const learnerId = learner.id || learner.uid;
        const learnerDocs = docsByLearner[learnerId] || [];
        const latestDocByCategory = {};
        const latestDocBySpreadsheetColumn = {};

        learnerDocs.forEach((docItem) => {
          const categoryKey = resolveDocumentCategoryKey(docItem);
          if (categoryKey && requiredCategorySet.has(categoryKey)) {
            const existing = latestDocByCategory[categoryKey];
            if (!existing) {
              latestDocByCategory[categoryKey] = docItem;
            } else {
              const existingDate = existing.uploadedAt instanceof Date ? existing.uploadedAt : new Date(existing.uploadedAt || 0);
              const currentDate = docItem.uploadedAt instanceof Date ? docItem.uploadedAt : new Date(docItem.uploadedAt || 0);
              if (currentDate >= existingDate) {
                latestDocByCategory[categoryKey] = docItem;
              }
            }
          }

          spreadsheetDocColumns.forEach((col) => {
            if (!col.matches(docItem)) return;
            const existing = latestDocBySpreadsheetColumn[col.key];
            if (!existing) {
              latestDocBySpreadsheetColumn[col.key] = docItem;
              return;
            }
            const existingDate = existing.uploadedAt instanceof Date ? existing.uploadedAt : new Date(existing.uploadedAt || 0);
            const currentDate = docItem.uploadedAt instanceof Date ? docItem.uploadedAt : new Date(docItem.uploadedAt || 0);
            if (currentDate >= existingDate) {
              latestDocBySpreadsheetColumn[col.key] = docItem;
            }
          });
        });

        const submittedRequiredSet = new Set();
        Object.entries(latestDocByCategory).forEach(([categoryKey, docItem]) => {
          const cellValue = getCategoryCellValue(docItem);
          if (cellValue !== 'Missing') {
            submittedRequiredSet.add(categoryKey);
          }
        });

        const categoryCells = spreadsheetDocColumns.map((col) => {
          categorySummary[col.key].total += 1;
          const value = getCategoryCellValue(latestDocBySpreadsheetColumn[col.key]);
          if (value !== 'Missing') {
            categorySummary[col.key].submitted += 1;
          }
          return value;
        });

        const values = [
          learner.displayName || learner.name || 'No Name',
          learner.idNumber || '',
          (() => {
            const learnerCohorts = getLearnerCohortIds(learner);
            const preferredCohortId = selectedCohortIds.find((id) => learnerCohorts.includes(id))
              || learner.cohortId
              || learnerCohorts[0];
            return getCohortName(preferredCohortId, learner.cohortName || 'Unassigned');
          })(),
          learner.gender || '',
          getActivityStatusLabel(learner),
          ...categoryCells
        ];

        return values;
      });

    const summaryRow = [
      'COLUMN SUMMARY',
      '',
      '',
      '',
      '',
      ...spreadsheetDocColumns.map((col) => {
        const stats = categorySummary[col.key];
        return `${stats.submitted}/${stats.total}`;
      }),
    ];

    const { default: ExcelJS } = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1', {
      views: [{ state: 'frozen', xSplit: 1, ySplit: 1 }]
    });

    worksheet.addRow(headers);
    dataRows.forEach((row) => worksheet.addRow(row));
    worksheet.addRow(summaryRow);

    worksheet.columns = headers.map((header, idx) => {
      const columnIndex = idx + 1;
      const widthMap = {
        1: 28,
        2: 16,
        3: 24,
        4: 12,
        5: 16
      };
      return {
        header,
        key: `c${columnIndex}`,
        width: widthMap[columnIndex] || (columnIndex >= 6 && columnIndex <= (5 + spreadsheetDocColumns.length) ? 22 : 20)
      };
    });

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9EAD3' } };
    headerRow.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    headerRow.height = 24;

    const docStatusOptions = ['Yes', 'Missing', 'Pending', 'Rejected', 'Error', 'Warning'];
    const docColumnStart = 6;
    const docColumnEnd = docColumnStart + spreadsheetDocColumns.length - 1;
    const firstDataRow = 2;
    const lastDataRow = 1 + dataRows.length;
    const summaryRowIndex = lastDataRow + 1;

    for (let r = firstDataRow; r <= lastDataRow; r += 1) {
      for (let c = docColumnStart; c <= docColumnEnd; c += 1) {
        const cell = worksheet.getRow(r).getCell(c);
        cell.dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [`"${docStatusOptions.join(',')}"`]
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };

        const value = String(cell.value || '');
        if (value === 'Yes') {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC6EFCE' } };
        } else if (value === 'Missing') {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
        } else if (value === 'Rejected' || value === 'Error') {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF4CCCC' } };
        } else if (value === 'Pending' || value === 'Warning') {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCE5CD' } };
        }
      }
    }

    const summary = worksheet.getRow(summaryRowIndex);
    summary.font = { bold: true };
    summary.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F3F3' } };

    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFD9D9D9' } },
          left: { style: 'thin', color: { argb: 'FFD9D9D9' } },
          bottom: { style: 'thin', color: { argb: 'FFD9D9D9' } },
          right: { style: 'thin', color: { argb: 'FFD9D9D9' } }
        };
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `cohort_submission_breakdown${getSelectedCohortSuffix()}_${new Date().toISOString().split('T')[0]}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export learner status report to PDF
  const exportLearnerStatusPDF = async () => {
    const filteredLearners = getFilteredLearners();
    const doc = new jsPDF();

    const loadImage = (url) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
        img.src = url;
      });

    try {
      const logo = await loadImage('/NPI_Logo.png');
      doc.addImage(logo, 'PNG', 14, 12, 30, 15);
    } catch (error) {
      devError('Error loading logo:', error);
    }

    // Title
    doc.setFontSize(18);
    doc.setTextColor(0, 102, 204); // Brand blue
    doc.text('Learner Status Report', 50, 22);

    // Subtitle with date and filter info
    doc.setFontSize(10);
    doc.setTextColor(100);
    const reportDate = new Date().toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    doc.text(`Generated: ${reportDate}`, 14, 30);

    // Filter information
    let filterInfo = 'Filter: ';
    if (selectedCohortIds.length) {
      filterInfo += getSelectedCohortLabel();
    } else if (learnerProgramFilter !== 'all') {
      const programName = programs.find(p => p.id === learnerProgramFilter)?.name || 'Selected Program';
      filterInfo += programName;
    } else {
      filterInfo += 'All Learners';
    }

    if (learnerFilter !== 'all') {
      const statusLabels = {
        'logged_in': 'Logged In',
        'never_logged_in': 'Never Logged In',
        'registered': 'Registered',
        'unregistered': 'Unregistered'
      };
      filterInfo += ` | Status: ${statusLabels[learnerFilter] || learnerFilter}`;
    }
    doc.text(filterInfo, 14, 36);

    // Summary stats
    const counts = getCohortLoginCounts();
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text(`Total Learners: ${counts.total}  |  Logged In: ${counts.loggedIn}  |  Never Logged In: ${counts.neverLoggedIn}`, 14, 44);

    // Table data
    const tableData = filteredLearners.map(learner => {
      const cohortName = cohorts.find(c => c.id === learner.cohortId)?.name || 'Unassigned';
      const status = learner.lastLoginAt ? 'Verified' : 'Unverified';
      const hasAddress =
        !!learner.streetAddress &&
        !!learner.city &&
        !!learner.province &&
        !!learner.postalCode;
      const addressConfirmed = learner.needsAddressVerification === false || !!learner.addressCompletedAt || hasAddress;

      return [
        learner.displayName || 'No Name',
        cohortName,
        status,
        addressConfirmed ? 'Yes' : 'No'
      ];
    });

    // Create table
    autoTable(doc, {
      startY: 52,
      head: [['Name', 'Cohort', 'Status', 'Address Confirmed']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [0, 102, 204],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 11
      },
      bodyStyles: {
        fontSize: 10
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250]
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 70 },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 }
      },
      margin: { left: 14, right: 14 },
      didDrawPage: function(data) {
        // Footer
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Access70 LMS - Page ${data.pageNumber}`,
          data.settings.margin.left,
          doc.internal.pageSize.height - 10
        );
      }
    });

    // Save PDF
    doc.save(`learner_status_report${getSelectedCohortSuffix()}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportConfirmedAttendancePDF = async (sessionId) => {
    try {
      setConfirmationError('');
      const session = sessions.find((item) => item.id === sessionId);
      if (!session) {
        setConfirmationError('Selected session not found.');
        return;
      }

      const attendanceResult = await attendanceService.getSessionAttendance(sessionId);
      if (!attendanceResult.success) {
        throw new Error(attendanceResult.error || 'Failed to load attendance.');
      }

      const confirmedRecords = attendanceResult.attendance.filter(
        (record) => record.source === 'learner-self-marked'
      );

      if (confirmedRecords.length === 0) {
        setConfirmationError('No confirmed attendance records found for this session.');
        return;
      }

      const learnersById = new Map(learners.map((learner) => [learner.id, learner]));
      const sessionCohortName = session.cohortName || cohorts.find(c => c.id === session.cohortId)?.name || 'Cohort';

      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text('Attendance Confirmation Report', 14, 18);

      doc.setFontSize(11);
      doc.setTextColor(80);
      doc.text(`Session: ${session.title || session.moduleName || 'Session'}`, 14, 26);
      doc.text(`Cohort: ${sessionCohortName}`, 14, 32);
      doc.text(`Date: ${session.scheduledDate?.toLocaleDateString?.() || 'Unknown'}`, 14, 38);
      if (session.notes) {
        doc.text(`Notes: ${session.notes}`, 14, 44);
        doc.text(`Generated: ${new Date().toLocaleString('en-ZA')}`, 14, 50);
      } else {
        doc.text(`Generated: ${new Date().toLocaleString('en-ZA')}`, 14, 44);
      }

      const rows = confirmedRecords.map((record, index) => {
        const learner = learnersById.get(record.learnerId);
        return [
          String(index + 1),
          learner?.displayName || 'Unknown',
          learner?.email || '',
          learner?.cohortName || sessionCohortName,
          record.markedAt?.toLocaleString?.('en-ZA') || 'Unknown'
        ];
      });

      autoTable(doc, {
        head: [['#', 'Learner', 'Email', 'Cohort', 'Confirmed At']],
        body: rows,
        startY: session.notes ? 58 : 52
      });

      const safeTitle = (session.title || session.moduleName || 'session').replace(/[^a-z0-9]+/gi, '_');
      doc.save(`attendance_confirmed_${safeTitle}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      devError('Error exporting confirmed attendance PDF:', err);
      setConfirmationError('Failed to export confirmed attendance list.');
    }
  };

  const normalizeHeader = (value) => (
    String(value || '').toLowerCase().replace(/[^a-z]/g, '')
  );

  const normalizeName = (value) => (
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
  );

  const splitCsvLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result.map((value) => value.trim());
  };

  const splitTeamsLine = (line) => {
    if (line.includes('\t')) {
      return line.split('\t').map((value) => value.trim());
    }
    return splitCsvLine(line);
  };

  const parseTeamsCsvText = (text) => {
    const lines = text.split(/\r?\n/);
    if (lines.length === 0) return [];

    const participantsIndex = lines.findIndex((line) => (
      /^\s*2\.\s*participants\s*$/i.test(line) || /^\s*participants\s*$/i.test(line)
    ));

    let headerLineIndex = -1;
    if (participantsIndex >= 0) {
      for (let i = participantsIndex + 1; i < lines.length; i += 1) {
        if (lines[i].trim()) {
          headerLineIndex = i;
          break;
        }
      }
    } else {
      headerLineIndex = lines.findIndex((line) => line.trim().length > 0);
    }

    if (headerLineIndex < 0) return [];

    const headers = splitTeamsLine(lines[headerLineIndex]);
    const headerMap = headers.reduce((acc, header, index) => {
      acc[normalizeHeader(header)] = index;
      return acc;
    }, {});

    const nameKeys = ['fullname', 'name', 'displayname', 'username', 'participantname'];
    const emailKeys = ['email', 'emailaddress', 'useremail', 'mail'];

    const nameIndex = nameKeys.map((key) => headerMap[key]).find((value) => value !== undefined);
    const emailIndex = emailKeys.map((key) => headerMap[key]).find((value) => value !== undefined);

    const rows = [];
    for (let i = headerLineIndex + 1; i < lines.length; i += 1) {
      const line = lines[i];
      if (!line.trim()) break;
      if (/^\s*3\./i.test(line) || /in-?meeting activities/i.test(line)) break;
      const columns = splitTeamsLine(line);
      const name = nameIndex !== undefined ? columns[nameIndex] : '';
      const email = emailIndex !== undefined ? columns[emailIndex] : '';
      if (!name && !email) continue;
      rows.push({ name, email });
    }
    return rows;
  };

  const parseExpectedEmails = () => (
    String(customSessionExpectedEmails || '')
      .split(/[\n,;]+/)
      .map((item) => item.trim().toLowerCase())
      .filter((item) => item && item.includes('@'))
  );

  const handleTeamsCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setTeamsCheckError('');
    setTeamsCheckResults(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result || '';
      const rows = parseTeamsCsvText(String(text));
      setTeamsCsvRows(rows);
    };
    reader.onerror = () => {
      setTeamsCheckError('Failed to read CSV file.');
    };
    reader.readAsText(file);
  };

  const handleCustomTeamsCsvUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setCustomCheckError('');
    setCustomCheckResults(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result || '';
      const rows = parseTeamsCsvText(String(text));
      setCustomTeamsCsvRows(rows);
    };
    reader.onerror = () => {
      setCustomCheckError('Failed to read CSV file.');
    };
    reader.readAsText(file);
  };

  const runTeamsAttendanceCheck = async () => {
    try {
      setTeamsCheckLoading(true);
      setTeamsCheckError('');
      setTeamsSessionSuccess('');

      if (!teamsCohortId) {
        setTeamsCheckError('Please select a cohort.');
        return;
      }
      if (teamsCsvRows.length === 0) {
        setTeamsCheckError('Please upload a Teams attendance CSV file.');
        return;
      }

      const cohortLearners = learners.filter((learner) => learner.cohortId === teamsCohortId);
      if (cohortLearners.length === 0) {
        setTeamsCheckError('No learners found for the selected cohort.');
        return;
      }

      const expectedByEmail = new Map();
      const expectedByName = new Map();
      cohortLearners.forEach((learner) => {
        const email = String(learner.email || '').toLowerCase();
        const name = normalizeName(learner.displayName || learner.name || '');
        if (email) expectedByEmail.set(email, learner);
        if (name) expectedByName.set(name, learner);
      });

      const matchedLearnerIds = new Set();
      const presentExpected = [];
      const unexpectedAttendees = [];

      teamsCsvRows.forEach((row) => {
        const email = String(row.email || '').toLowerCase();
        const nameKey = normalizeName(row.name);
        const matchedLearner = email ? expectedByEmail.get(email) : expectedByName.get(nameKey);

        if (matchedLearner) {
          if (!matchedLearnerIds.has(matchedLearner.id)) {
            matchedLearnerIds.add(matchedLearner.id);
            presentExpected.push({
              name: matchedLearner.displayName || matchedLearner.name || row.name || 'Unknown',
              email: matchedLearner.email || row.email || '',
              matchType: email && expectedByEmail.get(email) ? 'email' : 'name'
            });
          }
        } else {
          unexpectedAttendees.push({
            name: row.name || 'Unknown',
            email: row.email || '',
            reason: row.email ? 'Not in cohort' : 'Unmatched name'
          });
        }
      });

      const missingExpected = cohortLearners
        .filter((learner) => !matchedLearnerIds.has(learner.id))
        .map((learner) => ({
          name: learner.displayName || learner.name || 'Unknown',
          email: learner.email || ''
        }));

      setTeamsCheckResults({
        cohortName: cohorts.find(c => c.id === teamsCohortId)?.name || 'Cohort',
        totalExpected: cohortLearners.length,
        totalAttendees: teamsCsvRows.length,
        presentExpected,
        missingExpected,
        unexpectedAttendees
      });
    } catch (error) {
      devError('Error running Teams attendance check:', error);
      setTeamsCheckError('Failed to run Teams attendance check.');
    } finally {
      setTeamsCheckLoading(false);
    }
  };

  const createTeamsAttendanceSession = async () => {
    if (!teamsCheckResults || !teamsCohortId) {
      setTeamsCheckError('Run the cohort attendance check before creating the session.');
      return;
    }

    const scheduledDate = new Date(`${teamsSessionDate}T${teamsSessionTime}`);
    if (Number.isNaN(scheduledDate.getTime())) {
      setTeamsCheckError('Please enter a valid session date and time.');
      return;
    }

    try {
      setCreatingTeamsSession(true);
      setTeamsCheckError('');
      setTeamsSessionSuccess('');

      const cohort = cohorts.find((item) => item.id === teamsCohortId);
      const cohortLearners = learners.filter((learner) => learner.cohortId === teamsCohortId);
      const presentEmailSet = new Set(
        teamsCheckResults.presentExpected
          .map((item) => String(item.email || '').toLowerCase().trim())
          .filter(Boolean)
      );

      const sessionTitle = `Teams Attendance - ${cohort?.name || 'Cohort'} (${teamsSessionDate})`;
      const sessionResult = await attendanceService.createSession({
        title: sessionTitle,
        scheduledDate,
        cohortId: teamsCohortId,
        cohortName: cohort?.name || 'Cohort',
        programId: cohort?.programId || null,
        moduleId: null,
        moduleName: 'Teams Attendance',
        facilitatorId: currentUser?.uid || null,
        createdBy: currentUser?.uid || 'qa',
        status: 'completed',
        requiresAttendanceConfirmation: false,
        confirmationRequested: false
      });

      if (!sessionResult.success) {
        throw new Error(sessionResult.error || 'Failed to create Teams attendance session.');
      }

      const attendancePayload = cohortLearners.map((learner) => {
        const learnerEmail = String(learner.email || '').toLowerCase().trim();
        const isPresent = learnerEmail && presentEmailSet.has(learnerEmail);
        return {
          learnerId: learner.id,
          status: isPresent ? 'present' : 'absent',
          cohortId: learner.cohortId || teamsCohortId,
          moduleId: null,
          date: scheduledDate,
          source: isPresent ? 'teams-auto' : 'expected-list'
        };
      });

      if (attendancePayload.length > 0) {
        const markResult = await attendanceService.markAttendance(
          sessionResult.sessionId,
          attendancePayload,
          currentUser?.uid || 'qa'
        );
        if (!markResult.success) {
          throw new Error(markResult.error || 'Failed to record Teams attendance.');
        }
      }

      setTeamsSessionSuccess('Teams cohort attendance session created and written into the LMS.');
      await loadSessions();
      await loadPendingConfirmations();
    } catch (error) {
      devError('Error creating Teams attendance session:', error);
      setTeamsCheckError('Failed to create Teams attendance session: ' + error.message);
    } finally {
      setCreatingTeamsSession(false);
    }
  };

  const runCustomSessionCheck = () => {
    try {
      setCustomCheckLoading(true);
      setCustomCheckError('');
      setCustomSessionSuccess('');

      const expectedEmails = parseExpectedEmails();
      if (!customSessionTitle.trim()) {
        setCustomCheckError('Please enter a session title.');
        return;
      }
      if (!customSessionDate) {
        setCustomCheckError('Please select a session date.');
        return;
      }
      if (!customSessionTime) {
        setCustomCheckError('Please select a session time.');
        return;
      }
      if (!expectedEmails.length) {
        setCustomCheckError('Please provide at least one expected learner email.');
        return;
      }
      if (customTeamsCsvRows.length === 0) {
        setCustomCheckError('Please upload a Teams attendance CSV file.');
        return;
      }

      const learnersByEmail = new Map(
        learners.map((learner) => [String(learner.email || '').toLowerCase(), learner])
      );
      const expectedLearners = expectedEmails
        .map((email) => learnersByEmail.get(email))
        .filter(Boolean);
      const missingLearnerEmails = expectedEmails.filter((email) => !learnersByEmail.get(email));

      const expectedByEmail = new Map(
        expectedLearners.map((learner) => [String(learner.email || '').toLowerCase(), learner])
      );
      const expectedByName = new Map(
        expectedLearners
          .map((learner) => [normalizeName(learner.displayName || learner.name || ''), learner])
          .filter(([name]) => name)
      );

      const matchedLearnerIds = new Set();
      const presentExpected = [];
      const unexpectedAttendees = [];

      customTeamsCsvRows.forEach((row) => {
        const email = String(row.email || '').toLowerCase();
        const nameKey = normalizeName(row.name);
        const matchedLearner = email
          ? expectedByEmail.get(email)
          : expectedByName.get(nameKey);

        if (matchedLearner) {
          if (!matchedLearnerIds.has(matchedLearner.id)) {
            matchedLearnerIds.add(matchedLearner.id);
            presentExpected.push({
              id: matchedLearner.id,
              name: matchedLearner.displayName || matchedLearner.name || row.name || 'Unknown',
              email: matchedLearner.email || row.email || '',
              matchType: email && expectedByEmail.get(email) ? 'email' : 'name'
            });
          }
        } else {
          unexpectedAttendees.push({
            name: row.name || 'Unknown',
            email: row.email || '',
            reason: row.email ? 'Not in expected list' : 'Unmatched name'
          });
        }
      });

      const missingExpected = expectedLearners
        .filter((learner) => !matchedLearnerIds.has(learner.id))
        .map((learner) => ({
          id: learner.id,
          name: learner.displayName || learner.name || 'Unknown',
          email: learner.email || ''
        }));

      setCustomCheckResults({
        title: customSessionTitle.trim(),
        scheduledDate: customSessionDate,
        scheduledTime: customSessionTime,
        expectedEmails,
        expectedLearners,
        missingLearnerEmails,
        totalExpected: expectedEmails.length,
        totalExpectedLearners: expectedLearners.length,
        totalAttendees: customTeamsCsvRows.length,
        presentExpected,
        missingExpected,
        unexpectedAttendees
      });
    } catch (error) {
      devError('Error running custom session check:', error);
      setCustomCheckError('Failed to run attendance check.');
    } finally {
      setCustomCheckLoading(false);
    }
  };

  const createCustomSession = async () => {
    if (!customCheckResults) {
      setCustomCheckError('Run the attendance check before creating the session.');
      return;
    }

    const scheduledDate = new Date(`${customCheckResults.scheduledDate}T${customCheckResults.scheduledTime}`);
    if (Number.isNaN(scheduledDate.getTime())) {
      setCustomCheckError('Invalid session date or time.');
      return;
    }

    try {
      setCreatingCustomSession(true);
      setCustomCheckError('');
      setCustomSessionSuccess('');

      const uniqueCohortIds = Array.from(
        new Set(customCheckResults.expectedLearners.map((learner) => learner.cohortId).filter(Boolean))
      );
      const sessionResult = await attendanceService.createSession({
        title: customCheckResults.title,
        scheduledDate,
        cohortId: uniqueCohortIds.length === 1 ? uniqueCohortIds[0] : null,
        cohortIds: uniqueCohortIds,
        cohortName: 'Custom Session',
        moduleId: null,
        moduleName: 'Custom Session',
        facilitatorId: currentUser?.uid || null,
        createdBy: currentUser?.uid || 'qa',
        status: 'completed',
        requiresAttendanceConfirmation: true,
        confirmationRequested: false,
        assignedUserEmails: customCheckResults.expectedEmails
      });

      if (!sessionResult.success) {
        throw new Error(sessionResult.error || 'Failed to create session.');
      }

      const presentIds = new Set(customCheckResults.presentExpected.map((item) => item.id));
      const attendancePayload = customCheckResults.expectedLearners.map((learner) => ({
        learnerId: learner.id,
        status: presentIds.has(learner.id) ? 'present' : 'absent',
        cohortId: learner.cohortId || null,
        moduleId: null,
        date: scheduledDate,
        source: presentIds.has(learner.id) ? 'teams-auto' : 'expected-list'
      }));

      if (attendancePayload.length > 0) {
        const markResult = await attendanceService.markAttendance(
          sessionResult.sessionId,
          attendancePayload,
          currentUser?.uid || 'qa'
        );
        if (!markResult.success) {
          throw new Error(markResult.error || 'Failed to record attendance.');
        }
      }

      customCheckResults.expectedLearners.forEach((learner) => {
        NotificationService.notifyAttendanceConfirmation(learner, customCheckResults.title);
      });

      setCustomSessionSuccess('Custom session created. Attendance recorded and confirmations sent.');
      await loadSessions();
      await loadPendingConfirmations();
    } catch (error) {
      devError('Error creating custom session:', error);
      setCustomCheckError('Failed to create custom session: ' + error.message);
    } finally {
      setCreatingCustomSession(false);
    }
  };

  const exportCustomSessionPdf = () => {
    if (!customCheckResults) return;
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Custom Session Attendance Check', 14, 18);
    doc.setFontSize(11);
    doc.setTextColor(80);
    doc.text(`Session: ${customCheckResults.title}`, 14, 26);
    doc.text(`Date: ${customCheckResults.scheduledDate}`, 14, 32);
    doc.text(`Time: ${customCheckResults.scheduledTime}`, 14, 38);
    doc.text(`Expected emails: ${customCheckResults.totalExpected}`, 14, 44);
    doc.text(`Attendance rows: ${customCheckResults.totalAttendees}`, 14, 50);

    let startY = 58;
    autoTable(doc, {
      startY,
      head: [['Present (Expected)', 'Email', 'Match']],
      body: customCheckResults.presentExpected.map((row) => [row.name, row.email, row.matchType])
    });
    startY = doc.lastAutoTable.finalY + 8;

    autoTable(doc, {
      startY,
      head: [['Missing (Expected)', 'Email']],
      body: customCheckResults.missingExpected.map((row) => [row.name, row.email])
    });
    startY = doc.lastAutoTable.finalY + 8;

    autoTable(doc, {
      startY,
      head: [['Unexpected Attendee', 'Email', 'Reason']],
      body: customCheckResults.unexpectedAttendees.map((row) => [row.name, row.email, row.reason])
    });

    doc.save(`custom_session_attendance_${customCheckResults.scheduledDate}.pdf`);
  };

  const exportTeamsAttendancePdf = () => {
    if (!teamsCheckResults) return;

    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Teams Attendance Cohort Check', 14, 18);

    doc.setFontSize(11);
    doc.setTextColor(80);
    doc.text(`Cohort: ${teamsCheckResults.cohortName}`, 14, 26);
    doc.text(`Generated: ${new Date().toLocaleString('en-ZA')}`, 14, 32);
    doc.text(`Expected: ${teamsCheckResults.totalExpected}`, 14, 38);
    doc.text(`Attendees in CSV: ${teamsCheckResults.totalAttendees}`, 14, 44);

    let startY = 50;

    autoTable(doc, {
      startY,
      head: [['Present (Expected)', 'Email', 'Match']],
      body: teamsCheckResults.presentExpected.map((row) => [row.name, row.email, row.matchType])
    });
    startY = doc.lastAutoTable.finalY + 8;

    autoTable(doc, {
      startY,
      head: [['Missing (Expected)', 'Email']],
      body: teamsCheckResults.missingExpected.map((row) => [row.name, row.email])
    });
    startY = doc.lastAutoTable.finalY + 8;

    autoTable(doc, {
      startY,
      head: [['Unexpected Attendee', 'Email', 'Reason']],
      body: teamsCheckResults.unexpectedAttendees.map((row) => [row.name, row.email, row.reason])
    });

    doc.save(`teams_attendance_check_${teamsCheckResults.cohortName.replace(/[^a-z0-9]+/gi, '_')}.pdf`);
  };

  // Export document submissions report to CSV
  const exportSubmissionsReport = () => {
    const headers = ['Learner Name', 'Email', 'Program', 'Document Category', 'Status', 'Review Status', 'Uploaded Date'];
    const resolveDocumentCohortId = (doc) => doc.userInfo?.cohortId || doc.cohortId || null;
    const resolveDocumentProgramId = (doc) => {
      if (doc.userInfo?.programId) return doc.userInfo.programId;
      if (doc.programId) return doc.programId;
      const cohortId = resolveDocumentCohortId(doc);
      return cohortId ? (cohorts.find((cohort) => cohort.id === cohortId)?.programId || null) : null;
    };

    const scopedDocuments = documents.filter((doc) => {
      const cohortId = resolveDocumentCohortId(doc);
      const programId = resolveDocumentProgramId(doc);
      if (selectedCohortIds.length && !selectedCohortIds.includes(cohortId)) return false;
      if (learnerProgramFilter !== 'all' && programId !== learnerProgramFilter) return false;
      return true;
    });

    if (scopedDocuments.length === 0) {
      setError('No submissions found for the selected cohort/program filters.');
      return;
    }

    const csvRows = [
      headers.join(','),
      ...scopedDocuments.map(doc => {
        const status = getEffectiveStatus(doc);
        const reviewStatus = getEffectiveReviewStatus(doc) || 'Not Reviewed';
        const uploadedDate = doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : 'N/A';

        return [
          `"${(doc.userInfo?.displayName || 'Unknown').replace(/"/g, '""')}"`,
          `"${doc.userInfo?.email || ''}"`,
          `"${(doc.programName || 'Unassigned').replace(/"/g, '""')}"`,
          `"${(doc.category || 'N/A').replace(/"/g, '""')}"`,
          status,
          reviewStatus,
          uploadedDate
        ].join(',');
      })
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `submissions_report${getSelectedCohortSuffix()}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleProgramExpanded = (programId) => {
    setExpandedPrograms(prev => ({
      ...prev,
      [programId]: !prev[programId]
    }));
  };

  const handleDownloadProgramDocuments = async (programId, programName, documentsForProgram = []) => {
    try {
      setDownloadingProgramId(programId);
      setError('');
      setSuccess(`Preparing ${programName} documents as a zip...`);

      const result = await downloadProgramDocuments({
        programId,
        programName
      });
      setSuccess(`Prepared ${result.fileCount || documentsForProgram.length} documents for ${programName} as a zip.`);
    } catch (downloadError) {
      devError('Error downloading program documents:', downloadError);
      setSuccess('');
      setError(`Failed to download documents for ${programName}.`);
    } finally {
      setDownloadingProgramId('');
    }
  };

  const applyFilters = () => {
    let filtered = [...documents];
    devLog(`Applying filters. Total documents: ${documents.length}, Active tab: ${activeTab}`);

    // Filter by tab (active tab determines review status)
    if (activeTab === 'pending') {
      const beforeFilter = filtered.length;
      // Include documents that:
      // 1. Have reviewStatus === 'pending_review'
      // 2. Are flagged for review
      // 3. Have no reviewStatus at all (not yet reviewed)
      // 4. Have status === 'pending' (legacy status)
      filtered = filtered.filter(d => isPendingDoc(d));
      devLog(`Pending filter: ${beforeFilter} to ${filtered.length} documents`);
      devLog('Sample reviewStatus values:', documents.slice(0, 5).map(d => ({
        fileName: d.fileName,
        reviewStatus: d.reviewStatus,
        status: d.status,
        flagged: d.flaggedForReview
      })));
    } else if (activeTab === 'approved') {
      filtered = filtered.filter(d => getEffectiveReviewStatus(d) === 'qa_approved');
    } else if (activeTab === 'rejected') {
      filtered = filtered.filter(d => getEffectiveReviewStatus(d) === 'qa_rejected');
    }

    // Filter by student
    if (filterStudent !== 'all') {
      filtered = filtered.filter(d => d.userInfo?.id === filterStudent);
    }

    // Filter by cohort
    if (filterCohort !== 'all') {
      filtered = filtered.filter(d => (d.userInfo?.cohortId || d.cohortId) === filterCohort);
    }

    // Filter by validation status
    if (filterValidation !== 'all') {
      filtered = filtered.filter(d => d.validationStatus === filterValidation);
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const getRejectionText = (doc) => {
        const historyText = Array.isArray(doc.reviewHistory)
          ? doc.reviewHistory
              .map((item) => item?.comment || item?.notes || item?.reason || '')
              .filter(Boolean)
              .join(' ')
          : '';
        return [
          doc.rejectionReason,
          doc.reviewNotes,
          doc.qaNotes,
          doc.validationNotes,
          doc.latestComment,
          historyText
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
      };
      filtered = filtered.filter(d =>
        d.fileName?.toLowerCase().includes(term) ||
        d.userInfo?.displayName?.toLowerCase().includes(term) ||
        d.userInfo?.email?.toLowerCase().includes(term) ||
        d.category?.toLowerCase().includes(term) ||
        d.programName?.toLowerCase().includes(term) ||
        getRejectionText(d).includes(term)
      );
    }

    devLog(`Final filtered documents: ${filtered.length}`);
    setFilteredDocuments(filtered);
  };

  const handleReviewDocument = (document) => {
    setSelectedDocument(document);
    setShowReviewPanel(true);
  };

  const handleReviewComplete = () => {
    setShowReviewPanel(false);
    setSelectedDocument(null);
    setSuccess('Document reviewed successfully');
    loadDocuments();

    // Clear success message after 3 seconds
    setTimeout(() => setSuccess(''), 3000);
  };

  const attendanceFilterTerm = attendanceFacilitatorFilter.trim().toLowerCase();
  const getEffectiveMarkerId = (record) => {
    if (record?.markedBy) return record.markedBy;
    const session = sessions.find((item) => item.id === selectedSessionId);
    return session?.attendanceMarkedBy || session?.createdBy || session?.facilitatorId || null;
  };
  const filteredSessionAttendance = attendanceFilterTerm
    ? sessionAttendance.filter((record) => {
        const markerId = getEffectiveMarkerId(record);
        const marker = attendanceMarkerMap[markerId] || {};
        const markerText = `${marker.name || ''} ${marker.email || ''}`.toLowerCase();
        return markerText.includes(attendanceFilterTerm);
      })
    : sessionAttendance;

  const loadStatistics = async () => {
    try {
      setLoadingStats(true);
      setError('');

      const dateRange = {};
      if (startDate) dateRange.startDate = startDate;
      if (endDate) dateRange.endDate = endDate;

      const [validationStats, turnaround, rejections] = await Promise.all([
        qaService.getValidationStats(dateRange),
        qaService.getTurnaroundTimeStats(dateRange),
        qaService.getRejectionReasonsStats(dateRange)
      ]);

      setStats(validationStats);
      setTurnaroundStats(turnaround);
      setRejectionStats(rejections);
    } catch (err) {
      devError('Error loading statistics:', err);
      setError('Failed to load statistics: ' + err.message);
    } finally {
      setLoadingStats(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  const formatTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleTimeString();
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_review: { variant: 'warning', text: 'Pending Review' },
      qa_approved: { variant: 'success', text: 'Approved' },
      qa_rejected: { variant: 'danger', text: 'Rejected' },
      valid: { variant: 'success', text: 'Valid' },
      invalid: { variant: 'danger', text: 'Invalid' },
      warning: { variant: 'warning', text: 'Warning' }
    };

    const badge = badges[status] || { variant: 'secondary', text: status || 'Unknown' };
    return <Badge bg={badge.variant}>{badge.text}</Badge>;
  };

  return (
    <>
      {/* QA Navbar */}
      <QANavbar />

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">QA Dashboard</h1>
              <p className="mission-text mb-4">
                Ensure quality and accuracy through focused document review
              </p>
              <p className="mb-0">
                Review learner documents, validate submissions, and maintain quality standards across all learning materials.
              </p>
            </Col>
            <Col lg={4} className="d-flex align-items-center justify-content-end">
              <Button variant="light" onClick={loadDocuments}>
                Refresh
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">
        {/* Loading State */}
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3 text-muted">Loading QA dashboard data...</p>
          </div>
        )}

        {!loading && (
          <>

        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" onClose={() => setSuccess('')} dismissible>
            {success}
          </Alert>
        )}

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
        {/* All Documents by Program Tab */}
        <Tab eventKey="all-docs" title={`All Documents (${documents.length})`}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">All Documents Grouped by Program</h5>
              <Badge bg="primary">{documents.length} Total Documents</Badge>
            </Card.Header>
            <Card.Body>
              {/* Filters for All Documents */}
              <Row className="mb-4">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Search</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search documents, names, categories..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Filter by Student</Form.Label>
                    <Form.Select
                      value={filterStudent}
                      onChange={(e) => setFilterStudent(e.target.value)}
                    >
                      <option value="all">All Students</option>
                      {uniqueStudents.map(student => (
                        <option key={student.id} value={student.id}>
                          {student.name} ({student.email})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Filter by Cohort</Form.Label>
                    <Form.Select
                      value={filterCohort}
                      onChange={(e) => setFilterCohort(e.target.value)}
                    >
                      <option value="all">All Cohorts</option>
                      {uniqueCohorts.map((cohort) => (
                        <option key={cohort.id} value={cohort.id}>
                          {cohort.name || cohort.id}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Validation Status</Form.Label>
                    <Form.Select
                      value={filterValidation}
                      onChange={(e) => setFilterValidation(e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="valid">Valid</option>
                      <option value="invalid">Invalid</option>
                      <option value="warning">Warning</option>
                      <option value="pending">Pending</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {!groupedDocuments || Object.keys(groupedDocuments.grouped || {}).length === 0 ? (
                <Alert variant="info">
                  <Alert.Heading>No Documents Found</Alert.Heading>
                  <p className="mb-0">
                    No learner documents have been uploaded yet. Documents will appear here once learners start uploading their required documents.
                  </p>
                </Alert>
              ) : (
                <>
                  {/* Program Summary Cards */}
                  <Row className="mb-4">
                    {groupedDocuments.programs && groupedDocuments.programs.map(program => (
                      <Col md={4} lg={3} key={program.id} className="mb-3">
                        <Card
                          className={`h-100 cursor-pointer ${expandedPrograms[program.id] ? 'border-primary' : ''}`}
                          onClick={() => toggleProgramExpanded(program.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <Card.Body className="text-center">
                            <h6 className="mb-2">{program.name}</h6>
                            <h3 className="mb-0 text-primary">{program.count}</h3>
                            <small className="text-muted">documents</small>
                            <div className="mt-2">
                              {groupedDocuments.grouped[program.id] && (
                                <>
                                  <Badge bg="success" className="me-1">
                                    {groupedDocuments.grouped[program.id].stats.verified} verified
                                  </Badge>
                                  <Badge bg="warning" className="me-1">
                                    {groupedDocuments.grouped[program.id].stats.pending} pending
                                  </Badge>
                                  {groupedDocuments.grouped[program.id].stats.rejected > 0 && (
                                    <Badge bg="danger">
                                      {groupedDocuments.grouped[program.id].stats.rejected} rejected
                                    </Badge>
                                  )}
                                </>
                              )}
                            </div>
                            <div className="mt-3">
                              <Button
                                size="sm"
                                variant="outline-success"
                                disabled={downloadingProgramId === program.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadProgramDocuments(
                                    program.id,
                                    program.name,
                                    groupedDocuments.grouped[program.id]?.documents || []
                                  );
                                }}
                              >
                                {downloadingProgramId === program.id ? 'Downloading...' : 'Download Program Documents'}
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>

                  {/* Documents Table by Program */}
                  {Object.entries(groupedDocuments.grouped || {}).map(([programId, programData]) => {
                    // Filter documents within program based on active filters
                    let filteredProgramDocs = programData.documents;

                    // Apply student filter
                    if (filterStudent !== 'all') {
                      filteredProgramDocs = filteredProgramDocs.filter(d => d.userInfo?.id === filterStudent);
                    }

                    // Apply cohort filter
                    if (filterCohort !== 'all') {
                      filteredProgramDocs = filteredProgramDocs.filter(
                        d => (d.userInfo?.cohortId || d.cohortId) === filterCohort
                      );
                    }

                    // Apply validation filter
                    if (filterValidation !== 'all') {
                      filteredProgramDocs = filteredProgramDocs.filter(d => d.validationStatus === filterValidation);
                    }

                    // Apply search filter
                    if (searchTerm) {
                      const term = searchTerm.toLowerCase();
                      filteredProgramDocs = filteredProgramDocs.filter(d =>
                        d.fileName?.toLowerCase().includes(term) ||
                        d.userInfo?.displayName?.toLowerCase().includes(term) ||
                        d.userInfo?.email?.toLowerCase().includes(term) ||
                        d.category?.toLowerCase().includes(term)
                      );
                    }

                    // Skip programs with no matching documents after filtering
                    if (filteredProgramDocs.length === 0 && (filterStudent !== 'all' || filterCohort !== 'all' || filterValidation !== 'all' || searchTerm)) {
                      return null;
                    }

                    return (
                    <Card key={programId} className="mb-4">
                      <Card.Header
                        className="d-flex justify-content-between align-items-center"
                        onClick={() => toggleProgramExpanded(programId)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div>
                          <h6 className="mb-0">
                            {expandedPrograms[programId] ? '▼' : '▶'} {programData.programName}
                          </h6>
                          <small className="text-muted">
                            {filteredProgramDocs.length} of {programData.documents.length} documents
                          </small>
                        </div>
                        <div>
                          <Button
                            size="sm"
                            variant="outline-success"
                            className="me-2"
                            disabled={downloadingProgramId === programId}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadProgramDocuments(programId, programData.programName, filteredProgramDocs);
                            }}
                          >
                            {downloadingProgramId === programId ? 'Downloading...' : 'Download'}
                          </Button>
                          <Badge bg="success" className="me-1">{programData.stats.verified} verified</Badge>
                          <Badge bg="warning" className="me-1">{programData.stats.pending} pending</Badge>
                          {programData.stats.rejected > 0 && (
                            <Badge bg="danger">{programData.stats.rejected} rejected</Badge>
                          )}
                        </div>
                      </Card.Header>
                      {expandedPrograms[programId] && (
                        <Card.Body>
                          {filteredProgramDocs.length === 0 ? (
                            <Alert variant="info">No matching documents in this program.</Alert>
                          ) : (
                          <Table responsive hover size="sm">
                            <thead>
                              <tr>
                                <th>Learner</th>
                                <th>Cohort</th>
                                <th>Document</th>
                                <th>Category</th>
                                <th>Status</th>
                                <th>Uploaded</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredProgramDocs.map(doc => (
                                <tr key={doc.id}>
                                  <td>
                                    <strong>{doc.userInfo?.displayName || 'Unknown'}</strong>
                                    <br />
                                    <small className="text-muted">{doc.userInfo?.email}</small>
                                  </td>
                                  <td>
                                    <small>
                                      {getCohortName(doc.userInfo?.cohortId || doc.cohortId, doc.userInfo?.cohortName || doc.cohortName)}
                                    </small>
                                  </td>
                                  <td>{doc.fileName || doc.originalFileName || 'Unnamed'}</td>
                                  <td><small>{doc.category || 'N/A'}</small></td>
                                  <td>{getStatusBadge(getEffectiveStatus(doc))}</td>
                                  <td><small>{formatDate(doc.uploadedAt)}</small></td>
                                  <td>
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleReviewDocument(doc);
                                      }}
                                    >
                                      Review
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                          )}
                        </Card.Body>
                      )}
                    </Card>
                    );
                  })}
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Documents Tab */}
        <Tab eventKey="pending" title={`Pending Review (${documents.filter(d => isPendingDoc(d)).length})`}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Pending Reviews Grouped by Program</h5>
              <Badge bg="warning">{documents.filter((d) => isPendingDoc(d)).length} Pending</Badge>
            </Card.Header>
            <Card.Body>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Search</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search by file name, user, or category..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Validation Status</Form.Label>
                    <Form.Select
                      value={filterValidation}
                      onChange={(e) => setFilterValidation(e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="valid">Valid</option>
                      <option value="invalid">Invalid</option>
                      <option value="warning">Warning</option>
                      <option value="pending">Pending</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {!groupedDocuments || Object.keys(groupedDocuments.grouped || {}).length === 0 ? (
                <Alert variant="info">No pending documents for review</Alert>
              ) : (
                Object.entries(groupedDocuments.grouped || {}).map(([programId, programData]) => {
                  let filteredProgramDocs = (programData.documents || []).filter((d) => isPendingDoc(d));

                  if (filterStudent !== 'all') {
                    filteredProgramDocs = filteredProgramDocs.filter((d) => d.userInfo?.id === filterStudent);
                  }

                  if (filterCohort !== 'all') {
                    filteredProgramDocs = filteredProgramDocs.filter(
                      (d) => (d.userInfo?.cohortId || d.cohortId) === filterCohort
                    );
                  }

                  if (filterValidation !== 'all') {
                    filteredProgramDocs = filteredProgramDocs.filter((d) => d.validationStatus === filterValidation);
                  }

                  if (searchTerm) {
                    const term = searchTerm.toLowerCase();
                    filteredProgramDocs = filteredProgramDocs.filter((d) =>
                      d.fileName?.toLowerCase().includes(term) ||
                      d.userInfo?.displayName?.toLowerCase().includes(term) ||
                      d.userInfo?.email?.toLowerCase().includes(term) ||
                      d.category?.toLowerCase().includes(term) ||
                      d.programName?.toLowerCase().includes(term)
                    );
                  }

                  if (filteredProgramDocs.length === 0) return null;

                  return (
                    <Card key={programId} className="mb-3">
                      <Card.Header
                        className="d-flex justify-content-between align-items-center"
                        onClick={() => toggleProgramExpanded(programId)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div>
                          <h6 className="mb-0">
                            {expandedPrograms[programId] ? '▼' : '▶'} {programData.programName}
                          </h6>
                          <small className="text-muted">{filteredProgramDocs.length} pending documents</small>
                        </div>
                        <Badge bg="warning">{filteredProgramDocs.length}</Badge>
                      </Card.Header>
                      {expandedPrograms[programId] && (
                        <Card.Body>
                          <Table responsive hover>
                            <thead>
                              <tr>
                                <th>File Name</th>
                                <th>User</th>
                                <th>Category</th>
                                <th>Uploaded</th>
                                <th>Validation</th>
                                <th>Flagged By</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredProgramDocs.map((doc) => (
                                <tr key={doc.id}>
                                  <td>{doc.fileName || 'Unnamed'}</td>
                                  <td>
                                    {doc.userInfo?.displayName || 'Unknown'}
                                    <br />
                                    <small className="text-muted">{doc.userInfo?.email}</small>
                                  </td>
                                  <td>{doc.category || 'N/A'}</td>
                                  <td>
                                    {formatDate(doc.uploadedAt)}
                                    <br />
                                    <small className="text-muted">{formatTime(doc.uploadedAt)}</small>
                                  </td>
                                  <td>{getStatusBadge(doc.validationStatus)}</td>
                                  <td>{doc.flaggedReason || 'Not specified'}</td>
                                  <td>
                                    <Button
                                      size="sm"
                                      variant="primary"
                                      onClick={() => handleReviewDocument(doc)}
                                    >
                                      Review
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Card.Body>
                      )}
                    </Card>
                  );
                })
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="approved" title={`Approved (${documents.filter(d => getEffectiveReviewStatus(d) === 'qa_approved').length})`}>
          <Card>
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Select
                    value={filterCohort}
                    onChange={(e) => setFilterCohort(e.target.value)}
                  >
                    <option value="all">All Cohorts</option>
                    {uniqueCohorts.map((cohort) => (
                      <option key={cohort.id} value={cohort.id}>
                        {cohort.name || cohort.id}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>

              {filteredDocuments.length === 0 ? (
                <Alert variant="info">No approved documents</Alert>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>File Name</th>
                      <th>User</th>
                      <th>Category</th>
                      <th>Reviewed</th>
                      <th>Reviewed By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDocuments.map(doc => (
                      <tr key={doc.id}>
                        <td>{doc.fileName || 'Unnamed'}</td>
                        <td>{doc.userInfo?.displayName || 'Unknown'}</td>
                        <td>{doc.category || 'N/A'}</td>
                        <td>{formatDate(doc.reviewedAt)}</td>
                        <td>{doc.reviewedBy || 'N/A'}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleReviewDocument(doc)}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="rejected" title={`Rejected (${documents.filter(d => getEffectiveReviewStatus(d) === 'qa_rejected').length})`}>
          <Card>
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Select
                    value={filterCohort}
                    onChange={(e) => setFilterCohort(e.target.value)}
                  >
                    <option value="all">All Cohorts</option>
                    {uniqueCohorts.map((cohort) => (
                      <option key={cohort.id} value={cohort.id}>
                        {cohort.name || cohort.id}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>

              {filteredDocuments.length === 0 ? (
                <Alert variant="info">No rejected documents</Alert>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>File Name</th>
                      <th>User</th>
                      <th>Category</th>
                      <th>Reviewed</th>
                      <th>Rejection Reason</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDocuments.map(doc => (
                      <tr key={doc.id}>
                        <td>{doc.fileName || 'Unnamed'}</td>
                        <td>{doc.userInfo?.displayName || 'Unknown'}</td>
                        <td>{doc.category || 'N/A'}</td>
                        <td>{formatDate(doc.reviewedAt)}</td>
                        <td>{doc.rejectionReason || 'N/A'}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleReviewDocument(doc)}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Reports Tab */}
        <Tab eventKey="reports" title="Reports">
          <Card>
            <Card.Header>
              <h5>QA Statistics & Reports</h5>
            </Card.Header>
            <Card.Body>
              <Row className="mb-4">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                  <Button
                    variant="primary"
                    onClick={loadStatistics}
                    disabled={loadingStats}
                  >
                    {loadingStats ? 'Loading...' : 'Generate Report'}
                  </Button>
                </Col>
              </Row>

              {stats && (
                <>
                  <Row className="mb-4">
                    <Col md={6}>
                      <Card className="bg-light">
                        <Card.Body>
                          <h6>Validation Statistics</h6>
                          <Table size="sm" borderless>
                            <tbody>
                              <tr>
                                <td>Total Documents:</td>
                                <td className="text-end"><strong>{stats.total}</strong></td>
                              </tr>
                              <tr>
                                <td>Valid:</td>
                                <td className="text-end text-success">{stats.byValidationStatus.valid}</td>
                              </tr>
                              <tr>
                                <td>Invalid:</td>
                                <td className="text-end text-danger">{stats.byValidationStatus.invalid}</td>
                              </tr>
                              <tr>
                                <td>Warning:</td>
                                <td className="text-end text-warning">{stats.byValidationStatus.warning}</td>
                              </tr>
                              <tr>
                                <td>Pending:</td>
                                <td className="text-end text-muted">{stats.byValidationStatus.pending}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col md={6}>
                      <Card className="bg-light">
                        <Card.Body>
                          <h6>Review Statistics</h6>
                          <Table size="sm" borderless>
                            <tbody>
                              <tr>
                                <td>Pending Review:</td>
                                <td className="text-end text-warning">{stats.byReviewStatus.pending_review}</td>
                              </tr>
                              <tr>
                                <td>Approved:</td>
                                <td className="text-end text-success">{stats.byReviewStatus.qa_approved}</td>
                              </tr>
                              <tr>
                                <td>Rejected:</td>
                                <td className="text-end text-danger">{stats.byReviewStatus.qa_rejected}</td>
                              </tr>
                              <tr>
                                <td>Not Reviewed:</td>
                                <td className="text-end text-muted">{stats.byReviewStatus.not_reviewed}</td>
                              </tr>
                              <tr>
                                <td>Flagged:</td>
                                <td className="text-end text-info">{stats.flagged}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {turnaroundStats && (
                    <Row className="mb-4">
                      <Col>
                        <Card className="bg-light">
                          <Card.Body>
                            <h6>Turnaround Time (Hours)</h6>
                            <Table size="sm" borderless>
                              <tbody>
                                <tr>
                                  <td>Average:</td>
                                  <td className="text-end"><strong>{turnaroundStats.average.toFixed(2)}h</strong></td>
                                </tr>
                                <tr>
                                  <td>Median:</td>
                                  <td className="text-end">{turnaroundStats.median.toFixed(2)}h</td>
                                </tr>
                                <tr>
                                  <td>Fastest:</td>
                                  <td className="text-end text-success">{turnaroundStats.fastest.toFixed(2)}h</td>
                                </tr>
                                <tr>
                                  <td>Slowest:</td>
                                  <td className="text-end text-danger">{turnaroundStats.slowest.toFixed(2)}h</td>
                                </tr>
                                <tr>
                                  <td>Total Reviewed:</td>
                                  <td className="text-end">{turnaroundStats.total}</td>
                                </tr>
                              </tbody>
                            </Table>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  )}

                  {rejectionStats && rejectionStats.reasons.length > 0 && (
                    <Row>
                      <Col>
                        <Card className="bg-light">
                          <Card.Body>
                            <h6>Rejection Reasons</h6>
                            <Table size="sm" striped hover>
                              <thead>
                                <tr>
                                  <th>Reason</th>
                                  <th className="text-end">Count</th>
                                  <th className="text-end">Percentage</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rejectionStats.reasons.map((item, idx) => (
                                  <tr key={idx}>
                                    <td>{item.reason}</td>
                                    <td className="text-end">{item.count}</td>
                                    <td className="text-end">{item.percentage}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                            <p className="text-muted mb-0">
                              Total Rejected: {rejectionStats.totalRejected}
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Progress Reports Tab */}
        <Tab eventKey="progress-reports" title="Progress Reports">
          <Card>
            <Card.Header>
              <h5>Generate Progress Reports</h5>
            </Card.Header>
            <Card.Body>
              <Alert variant="info" className="mb-4">
                <strong>QA Progress Reports:</strong> Generate reports on learner progress,
                document verification status, and completion rates.
              </Alert>
              <ProgressReportGenerator />
            </Card.Body>
          </Card>
        </Tab>

        {/* QA Reports Tab */}
        <Tab eventKey="qa-reports" title="QA Reports">
          <QAReportCenter />
        </Tab>
        <Tab eventKey="qa-submissions" title="Submissions">
          <div className="mt-4">
            <SubmissionRequestReviewPanel />
            <TemplateReviewPanel />
          </div>
        </Tab>

        <Tab eventKey="qa-document-upload" title="Document Upload">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Upload Documents for Learners</h5>
            </Card.Header>
            <Card.Body>
              <AdminDocumentUpload />
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="qa-resources" title="Resources">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Session Resources</h5>
            </Card.Header>
            <Card.Body>
              <PreviousSessionResources />
            </Card.Body>
          </Card>
        </Tab>

        {/* Learner Registration Status Tab */}
        <Tab
          eventKey="learner-status"
          title={`Learner Status (${getLearnerCounts().total})`}
          onEnter={() => {
            loadLearners();
          }}
        >
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Learner Registration Status</h5>
                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" size="sm" onClick={exportActivationReport}>
                    Export Login Report
                  </Button>
                  <Button variant="primary" size="sm" onClick={exportMasterSheet}>
                    Export Master Sheet
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {/* Summary Cards */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="text-center bg-light">
                    <Card.Body>
                      <h3 className="text-primary">{getLearnerCounts().total}</h3>
                      <p className="mb-0 text-muted">Total Learners</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center bg-success bg-opacity-10">
                    <Card.Body>
                      <h3 className="text-dark">{getLearnerCounts().loggedIn.toLocaleString()}</h3>
                      <p className="mb-0 text-muted">Logged In Learners</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center bg-danger bg-opacity-10">
                    <Card.Body>
                      <h3 className="text-dark">{getLearnerCounts().neverLoggedIn.toLocaleString()}</h3>
                      <p className="mb-0 text-muted">Never Logged In</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center bg-warning bg-opacity-10">
                    <Card.Body>
                      <h3 className="text-dark">{getLearnerCounts().unregistered.toLocaleString()}</h3>
                      <p className="mb-0 text-muted">Unregistered Learners</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">Report Scope</h5>
                </Card.Header>
                <Card.Body>
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Program</Form.Label>
                        <Form.Select
                          value={learnerProgramFilter}
                          onChange={(e) => handleProgramFilterChange(e.target.value)}
                        >
                          <option value="all">All Programs</option>
                          {programs.map(program => (
                            <option key={program.id} value={program.id}>
                              {program.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label>Cohorts</Form.Label>
                        <div className="d-flex gap-2 flex-wrap mb-2">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={selectAllCohorts}
                            disabled={getAvailableCohorts().length === 0}
                          >
                            Select all
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={clearCohortSelection}
                            disabled={selectedCohortIds.length === 0}
                          >
                            Clear
                          </Button>
                          <Badge bg="light" text="dark" className="align-self-center">
                            {selectedCohortIds.length || 'All'} selected
                          </Badge>
                        </div>
                        <div className="border rounded p-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                          {getAvailableCohorts().map((cohort) => (
                            <Form.Check
                              key={cohort.id}
                              type="checkbox"
                              id={`cohort-filter-${cohort.id}`}
                              label={cohort.name}
                              checked={selectedCohortIds.includes(cohort.id)}
                              onChange={() => toggleCohortSelection(cohort.id)}
                            />
                          ))}
                          {getAvailableCohorts().length === 0 && (
                            <div className="text-muted">No cohorts available for this program.</div>
                          )}
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Alert variant="info" className="mb-0">
                    <strong>Current scope:</strong>{' '}
                    Program: {learnerProgramFilter !== 'all'
                      ? (programs.find(p => p.id === learnerProgramFilter)?.name || 'Selected Program')
                      : 'All Programs'} | Cohorts: {getSelectedCohortLabel()}
                  </Alert>
                </Card.Body>
              </Card>

              {/* Cohort-specific Login Stats */}
              {(selectedCohortIds.length > 0 || learnerProgramFilter !== 'all') && (
                <Alert variant="info" className="mb-4">
                  <strong>Filtered Stats:</strong>{' '}
                  {getCohortLoginCounts().total} learners | {' '}
                  <Badge bg="success">{getCohortLoginCounts().loggedIn} Logged In</Badge>{' '}
                  <Badge bg="danger">{getCohortLoginCounts().neverLoggedIn} Never Logged In</Badge>
                </Alert>
              )}

              {/* Filters */}
              <Row className="mb-4">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Status Filter</Form.Label>
                    <Form.Select
                      value={learnerFilter}
                      onChange={(e) => setLearnerFilter(e.target.value)}
                    >
                      <option value="all">All Learners</option>
                      <option value="logged_in">Logged In</option>
                      <option value="never_logged_in">Never Logged In</option>
                      <option value="registered">Registered (Email Verified)</option>
                      <option value="unregistered">Unregistered</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Submission Filter</Form.Label>
                    <Form.Select
                      value={submissionStatusFilter}
                      onChange={(e) => setSubmissionStatusFilter(e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="submitted">Has Submitted</option>
                      <option value="not_submitted">Not Submitted</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Search</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search by name, email, or ID..."
                      value={learnerSearchTerm}
                      onChange={(e) => setLearnerSearchTerm(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={2} className="d-flex align-items-end">
                  <Button
                    variant="outline-primary"
                    onClick={loadLearners}
                    disabled={loadingLearners}
                  >
                    {loadingLearners ? 'Loading...' : 'Refresh'}
                  </Button>
                </Col>
              </Row>

              {/* Results count */}
              <div className="mb-3">
                <Badge bg="info">
                  Showing {getFilteredLearners().length} of {getBaseFilteredLearners().length} learners
                </Badge>
              </div>

              {/* Learners Table */}
              {loadingLearners ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                  <p className="mt-2">Loading learners...</p>
                </div>
              ) : getFilteredLearners().length === 0 ? (
                <Alert variant="info">
                  {learners.length === 0
                    ? 'Click "Refresh" to load learner data.'
                    : 'No learners match the current filters.'}
                </Alert>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Cohort</th>
                      <th>Submissions</th>
                      <th>Login Status</th>
                      <th>Last Login</th>
                      <th>Registration</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredLearners().map(learner => (
                      <tr key={learner.id}>
                        <td>
                          <strong>{learner.displayName || 'No Name'}</strong>
                          <br />
                          <small className="text-muted">{learner.idNumber || 'No ID'}</small>
                        </td>
                        <td>
                          <a href={`mailto:${learner.email}`}>{learner.email}</a>
                        </td>
                        <td>
                          <small>{getCohortName(learner.cohortId)}</small>
                        </td>
                        <td>
                          {(() => {
                            const stats = getLearnerSubmissionStats(learner);
                            return (
                              <div>
                                <Badge bg="success" className="me-1">{stats.submittedCount} submitted</Badge>
                                <Badge bg={stats.outstandingCount > 0 ? 'danger' : 'secondary'}>
                                  {stats.outstandingCount} outstanding
                                </Badge>
                              </div>
                            );
                          })()}
                        </td>
                        <td>
                          {learner.lastLoginAt ? (
                            <Badge bg="success">Logged In</Badge>
                          ) : (
                            <Badge bg="danger">Never</Badge>
                          )}
                        </td>
                        <td>
                          <small>
                            {learner.lastLoginAt?.toDate
                              ? learner.lastLoginAt.toDate().toLocaleDateString()
                              : (learner.lastLoginAt
                                ? new Date(learner.lastLoginAt).toLocaleDateString()
                                : 'N/A')}
                          </small>
                        </td>
                        <td>
                          {learner.emailVerified ? (
                            <Badge bg="success">Verified</Badge>
                          ) : (
                            <Badge bg="warning" text="dark">Pending</Badge>
                          )}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => openLearnerSubmissionBreakdown(learner)}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              {/* Export and Email Actions */}
              <div className="mt-4 d-flex gap-3 flex-wrap">
                <Button
                  variant="danger"
                  onClick={exportLearnerStatusPDF}
                  disabled={getFilteredLearners().length === 0}
                >
                  Export Status Report (PDF)
                </Button>
                <Button
                  variant="success"
                  onClick={exportActivationReport}
                  disabled={getFilteredLearners().length === 0}
                >
                  Export Full Report (CSV)
                </Button>
                <Button
                  variant="primary"
                  onClick={exportSubmissionsReport}
                  disabled={documents.length === 0}
                >
                  Export Submissions Report (CSV)
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={exportCohortSubmissionSpreadsheet}
                  disabled={getBaseFilteredLearners().length === 0}
                >
                  Export Cohort Submission Breakdown (XLSX)
                </Button>
              </div>

              {selectedCohortIds.length > 0 && (
                <Alert variant="success" className="mt-3">
                  <strong>Tip:</strong> The exported XLSX will include only learners from the selected cohorts: <strong>{getSelectedCohortLabel()}</strong>
                </Alert>
              )}

              {getFilteredLearners().length > 0 && learnerFilter === 'unregistered' && (
                <Alert variant="info" className="mt-3">
                  <strong>Tip:</strong> You can use the{' '}
                  <Button
                    variant="link"
                    className="p-0"
                    onClick={() => navigate('/email-composer')}
                  >
                    Email Composer
                  </Button>{' '}
                  to send reminder emails to unregistered learners.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Modal show={showLearnerSubmissionModal} onHide={() => setShowLearnerSubmissionModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Learner Submissions Breakdown</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedLearnerSubmission && (
              <>
                <p className="mb-1"><strong>Learner:</strong> {selectedLearnerSubmission.learner.displayName || selectedLearnerSubmission.learner.email}</p>
                <p className="mb-3"><strong>Email:</strong> {selectedLearnerSubmission.learner.email || 'N/A'}</p>
                <Row>
                  <Col md={6}>
                    <Card className="mb-3">
                      <Card.Header><strong>Submitted ({selectedLearnerSubmission.submittedCount})</strong></Card.Header>
                      <Card.Body>
                        {selectedLearnerSubmission.submittedCategories.length === 0 ? (
                          <span className="text-muted">No required documents submitted.</span>
                        ) : (
                          <ul className="mb-0">
                            {selectedLearnerSubmission.submittedCategories.map((categoryKey) => (
                              <li key={`submitted-${categoryKey}`}>
                                {DOCUMENT_CATEGORIES[categoryKey]?.name || categoryKey}
                              </li>
                            ))}
                          </ul>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="mb-3">
                      <Card.Header><strong>Outstanding ({selectedLearnerSubmission.outstandingCount})</strong></Card.Header>
                      <Card.Body>
                        {selectedLearnerSubmission.outstandingCategories.length === 0 ? (
                          <span className="text-success">All required documents submitted.</span>
                        ) : (
                          <ul className="mb-0">
                            {selectedLearnerSubmission.outstandingCategories.map((categoryKey) => (
                              <li key={`outstanding-${categoryKey}`}>
                                {DOCUMENT_CATEGORIES[categoryKey]?.name || categoryKey}
                              </li>
                            ))}
                          </ul>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowLearnerSubmissionModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Tab
          eventKey="qa-attendance"
          title="Attendance"
          onEnter={() => {
            loadSessions();
            loadModules();
            loadPendingConfirmations();
          }}
        >
          <Card>
            <Card.Header>
              <h5 className="mb-0">Attendance</h5>
            </Card.Header>
            <Card.Body>
              {/* Session Attendance Report */}
              <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Session Attendance Report</h5>
                  <Button variant="outline-secondary" size="sm" onClick={loadSessions}>
                    Refresh Sessions
                  </Button>
                </Card.Header>
                <Card.Body>
                  {sessionAttendanceError && (
                    <Alert variant="danger" onClose={() => setSessionAttendanceError('')} dismissible>
                      {sessionAttendanceError}
                    </Alert>
                  )}

                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Facilitator</Form.Label>
                        <Form.Select
                          value={selectedAttendanceFacilitatorId}
                          onChange={(e) => {
                            setSelectedAttendanceFacilitatorId(e.target.value);
                            setSelectedSessionId('');
                            setSessionAttendance([]);
                          }}
                          disabled={loadingSessions}
                        >
                          <option value="">Choose facilitator...</option>
                          {attendanceFacilitators
                            .slice()
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .map((facilitator) => (
                              <option key={facilitator.id} value={facilitator.id}>
                                {facilitator.name} {facilitator.email ? `(${facilitator.email})` : ''}
                              </option>
                            ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Attendance Session</Form.Label>
                        <Form.Select
                          value={selectedSessionId}
                          onChange={(e) => setSelectedSessionId(e.target.value)}
                          disabled={loadingSessions || !selectedAttendanceFacilitatorId}
                        >
                          <option value="">
                            {selectedAttendanceFacilitatorId ? 'Choose a session...' : 'Select facilitator first'}
                          </option>
                          {getFilteredSessions()
                            .filter((session) => {
                              if (!selectedAttendanceFacilitatorId) return false;
                              const markerId =
                                session.attendanceMarkedBy ||
                                session.createdBy ||
                                session.facilitatorId ||
                                null;
                              return markerId === selectedAttendanceFacilitatorId;
                            })
                            .slice()
                            .sort((a, b) => {
                              const dateA = a.scheduledDate ? a.scheduledDate.getTime() : 0;
                              const dateB = b.scheduledDate ? b.scheduledDate.getTime() : 0;
                              return dateA - dateB;
                            })
                            .map((session) => {
                            const labelDate = session.scheduledDate
                              ? session.scheduledDate.toLocaleDateString()
                              : 'Unknown date';
                            return (
                              <option key={session.id} value={session.id}>
                                {(session.cohortName || cohorts.find(c => c.id === session.cohortId)?.name || 'Session')} - {labelDate}
                              </option>
                            );
                          })}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4} className="d-flex align-items-end gap-2">
                      <Button
                        variant="primary"
                        onClick={loadSessionAttendance}
                        disabled={loadingSessionAttendance || !selectedSessionId}
                      >
                        {loadingSessionAttendance ? 'Loading...' : 'Generate Attendance Report'}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={downloadAttendanceCsv}
                        disabled={!selectedSessionId || filteredSessionAttendance.length === 0}
                      >
                        Download CSV
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={downloadAttendancePdf}
                        disabled={!selectedSessionId || filteredSessionAttendance.length === 0}
                      >
                        Download PDF
                      </Button>
                    </Col>
                  </Row>

                  {sessionAttendance.length > 0 && (
                    <>
                      <Row className="mb-3">
                        {(() => {
                          const selectedSession = sessions.find((session) => session.id === selectedSessionId);
                          const sessionCohortIds = selectedSession?.cohortIds?.length
                            ? selectedSession.cohortIds
                            : (selectedSession?.cohortId ? [selectedSession.cohortId] : []);
                          const cohortLearners = sessionCohortIds.length
                            ? learners.filter((learner) => {
                                if (!isActiveLearner(learner)) return false;
                                const learnerCohortIds = [
                                  learner.cohortId,
                                  ...(Array.isArray(learner.cohortIds) ? learner.cohortIds : [])
                                ].filter(Boolean);
                                return learnerCohortIds.some((id) => sessionCohortIds.includes(id));
                              })
                            : [];
                          const attendanceLearnerIds = Array.from(
                            new Set(sessionAttendance.map((record) => record.learnerId).filter(Boolean))
                          );
                          const attendanceLearners = attendanceLearnerIds
                            .map((id) => learners.find((l) => l.uid === id || l.id === id))
                            .filter(Boolean);
                          const attendanceActive = attendanceLearners.filter(isActiveLearner);
                          const expectedTotal = sessionCohortIds.length
                            ? cohortLearners.length
                            : (attendanceActive.length || attendanceLearnerIds.length || 0);
                          const present = filteredSessionAttendance.filter(r => r.status === 'present').length;
                          const absent = filteredSessionAttendance.filter(r => r.status === 'absent').length;
                          const excused = filteredSessionAttendance.filter(r => r.status === 'excused').length;
                          const incomplete = filteredSessionAttendance.filter(r => r.status === 'incomplete').length;
                          return (
                            <>
                              <Col md={3}>
                                <Card className="text-center bg-light">
                                  <Card.Body>
                                    <h3 className="text-primary">{expectedTotal}</h3>
                                    <p className="mb-0 text-muted">Cohort Size</p>
                                  </Card.Body>
                                </Card>
                              </Col>
                              <Col md={3}>
                                <Card className="text-center bg-success bg-opacity-10">
                                  <Card.Body>
                                    <h3 className="text-dark">{present}/{expectedTotal}</h3>
                                    <p className="mb-0 text-muted">Present</p>
                                  </Card.Body>
                                </Card>
                              </Col>
                              <Col md={3}>
                                <Card className="text-center bg-danger bg-opacity-10">
                                  <Card.Body>
                                    <h3 className="text-dark">{absent}/{expectedTotal}</h3>
                                    <p className="mb-0 text-muted">Absent</p>
                                  </Card.Body>
                                </Card>
                              </Col>
                              <Col md={3}>
                                <Card className="text-center bg-warning bg-opacity-10">
                                  <Card.Body>
                                    <h3 className="text-dark">{excused + incomplete}/{expectedTotal}</h3>
                                    <p className="mb-0 text-muted">Excused/Incomplete</p>
                                  </Card.Body>
                                </Card>
                              </Col>
                            </>
                          );
                        })()}
                      </Row>

                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Learner</th>
                            <th>Email</th>
                            <th>Status</th>
                            <th>Marked At</th>
                            <th>Source</th>
                            <th>Marked By</th>
                          </tr>
                        </thead>
                          <tbody>
                          {filteredSessionAttendance.map((record) => {
                            const learner =
                              learners.find(l => l.uid === record.learnerId) ||
                              learners.find(l => l.id === record.learnerId);
                            const markerId = getEffectiveMarkerId(record);
                            const marker = attendanceMarkerMap[markerId];
                            return (
                              <tr key={record.id}>
                                <td>{learner?.displayName || 'Unknown'}</td>
                                <td>{learner?.email || 'N/A'}</td>
                                <td className="text-capitalize">{record.status || 'unknown'}</td>
                                <td>
                                  {record.markedAt?.toDate
                                    ? record.markedAt.toDate().toLocaleString()
                                    : (record.markedAt ? new Date(record.markedAt).toLocaleString() : 'N/A')}
                                </td>
                                <td>{record.source || 'manual'}</td>
                                <td>{marker?.name || markerId || 'N/A'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </>
                  )}
                </Card.Body>
              </Card>

              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">Teams Attendance Cohort Check</h5>
                </Card.Header>
                <Card.Body>
                  {teamsCheckError && (
                    <Alert variant="danger" onClose={() => setTeamsCheckError('')} dismissible>
                      {teamsCheckError}
                    </Alert>
                  )}
                  {teamsSessionSuccess && (
                    <Alert variant="success" onClose={() => setTeamsSessionSuccess('')} dismissible>
                      {teamsSessionSuccess}
                    </Alert>
                  )}
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Select Cohort</Form.Label>
                        <Form.Select
                          value={teamsCohortId}
                          onChange={(e) => setTeamsCohortId(e.target.value)}
                        >
                          <option value="">Choose a cohort...</option>
                          {cohorts.map((cohort) => (
                            <option key={cohort.id} value={cohort.id}>
                              {cohort.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Upload Teams CSV</Form.Label>
                        <Form.Control type="file" accept=".csv" onChange={handleTeamsCsvUpload} />
                        <Form.Text className="text-muted">
                          CSV should include name or email columns.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4} className="d-flex align-items-end">
                      <Button
                        variant="primary"
                        onClick={runTeamsAttendanceCheck}
                        disabled={teamsCheckLoading}
                      >
                        {teamsCheckLoading ? 'Checking...' : 'Run Check'}
                      </Button>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Session Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={teamsSessionDate}
                          onChange={(e) => setTeamsSessionDate(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Session Time</Form.Label>
                        <Form.Control
                          type="time"
                          value={teamsSessionTime}
                          onChange={(e) => setTeamsSessionTime(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="d-flex align-items-end justify-content-end">
                      <Button
                        variant="success"
                        onClick={createTeamsAttendanceSession}
                        disabled={!teamsCheckResults || creatingTeamsSession}
                      >
                        {creatingTeamsSession ? 'Creating Session...' : 'Create LMS Session From Teams Check'}
                      </Button>
                    </Col>
                  </Row>

                  {teamsCsvRows.length > 0 && (
                    <Alert variant="info">
                      Loaded {teamsCsvRows.length} attendance rows from CSV.
                    </Alert>
                  )}

                  {teamsCheckResults && (
                    <>
                      <Row className="mb-3">
                        <Col md={3}>
                          <Card className="text-center bg-light">
                            <Card.Body>
                              <h4 className="text-primary">{teamsCheckResults.totalExpected}</h4>
                              <p className="mb-0 text-muted">Expected Learners</p>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="text-center bg-success bg-opacity-10">
                            <Card.Body>
                              <h4 className="text-success">{teamsCheckResults.presentExpected.length}</h4>
                              <p className="mb-0 text-muted">Present (Expected)</p>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="text-center bg-danger bg-opacity-10">
                            <Card.Body>
                              <h4 className="text-danger">{teamsCheckResults.missingExpected.length}</h4>
                              <p className="mb-0 text-muted">Missing</p>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="text-center bg-warning bg-opacity-10">
                            <Card.Body>
                              <h4 className="text-warning">{teamsCheckResults.unexpectedAttendees.length}</h4>
                              <p className="mb-0 text-muted">Unexpected</p>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>

                      <div className="d-flex justify-content-end mb-3">
                        <Button variant="outline-secondary" onClick={exportTeamsAttendancePdf}>
                          Export PDF
                        </Button>
                      </div>

                      <Row className="mb-3">
                        <Col md={6}>
                          <h6>Present (Expected)</h6>
                          <Table size="sm" bordered>
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Match</th>
                              </tr>
                            </thead>
                            <tbody>
                              {teamsCheckResults.presentExpected.map((row, index) => (
                                <tr key={`${row.email}-${index}`}>
                                  <td>{row.name}</td>
                                  <td>{row.email}</td>
                                  <td>{row.matchType}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Col>
                        <Col md={6}>
                          <h6>Unexpected Attendees</h6>
                          <Table size="sm" bordered>
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {teamsCheckResults.unexpectedAttendees.map((row, index) => (
                                <tr key={`${row.email}-${index}`}>
                                  <td>{row.name}</td>
                                  <td>{row.email}</td>
                                  <td>{row.reason}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Col>
                      </Row>

                      <h6>Missing (Expected)</h6>
                      <Table size="sm" bordered>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teamsCheckResults.missingExpected.map((row, index) => (
                            <tr key={`${row.email}-${index}`}>
                              <td>{row.name}</td>
                              <td>{row.email}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}
                </Card.Body>
              </Card>

              <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Attendance Confirmations Pending</h5>
                  <Button variant="outline-secondary" size="sm" onClick={loadPendingConfirmations}>
                    Refresh
                  </Button>
                </Card.Header>
                <Card.Body>
                  {confirmationError && (
                    <Alert variant="danger" onClose={() => setConfirmationError('')} dismissible>
                      {confirmationError}
                    </Alert>
                  )}
                  {loadingConfirmations ? (
                    <div className="text-center py-3">Loading confirmations...</div>
                  ) : confirmationSessions.length === 0 ? (
                    <Alert variant="info" className="mb-0">No pending attendance confirmations.</Alert>
                  ) : (
                    <Table responsive hover size="sm">
                      <thead>
                        <tr>
                          <th>Session</th>
                          <th>Cohort</th>
                          <th>Date</th>
                          <th>Confirmed</th>
                          <th>Pending</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {confirmationSessions.map((session) => (
                          <tr key={session.id}>
                            <td>{session.title}</td>
                            <td>{session.cohortName}</td>
                            <td>{session.scheduledDate}</td>
                            <td>{session.confirmedCount}/{session.expectedCount}</td>
                            <td>{session.pendingCount}</td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => exportConfirmedAttendancePDF(session.id)}
                              >
                                Export Confirmed PDF
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>

              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">Create Historic Session Attendance</h5>
                </Card.Header>
                <Card.Body>
                  {historicSessionSuccess && (
                    <Alert variant="success" onClose={() => setHistoricSessionSuccess('')} dismissible>
                      {historicSessionSuccess}
                    </Alert>
                  )}
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Cohort</Form.Label>
                        <Form.Select
                          value={historicCohortId}
                          onChange={(e) => {
                            setHistoricCohortId(e.target.value);
                            setHistoricAttendanceDraft({});
                          }}
                        >
                          <option value="">Select cohort...</option>
                          {cohorts.map((cohort) => (
                            <option key={cohort.id} value={cohort.id}>
                              {cohort.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Module</Form.Label>
                        <Form.Select
                          value={historicModuleId}
                          onChange={(e) => setHistoricModuleId(e.target.value)}
                          disabled={loadingModules}
                        >
                          <option value="">Select module...</option>
                          {modules.map((module) => (
                            <option key={module.id} value={module.id}>
                              {module.moduleName || module.title || module.id}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Session Title</Form.Label>
                        <Form.Control
                          type="text"
                          value={historicSessionTitle}
                          onChange={(e) => setHistoricSessionTitle(e.target.value)}
                          placeholder="Optional custom title"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={historicSessionDate}
                          onChange={(e) => setHistoricSessionDate(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Time</Form.Label>
                        <Form.Control
                          type="time"
                          value={historicSessionTime}
                          onChange={(e) => setHistoricSessionTime(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4} className="d-flex align-items-end">
                      <Button
                        variant="primary"
                        onClick={handleCreateHistoricSession}
                        disabled={savingHistoricSession}
                      >
                        {savingHistoricSession
                          ? 'Saving...'
                          : (historicRequestConfirmation
                            ? 'Create Session & Request Confirmation'
                            : 'Create Session & Save Attendance')}
                      </Button>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Session Notes</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={historicSessionNotes}
                          onChange={(e) => setHistoricSessionNotes(e.target.value)}
                          placeholder="Add notes for this historic session"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Check
                        type="checkbox"
                        id="historic-request-confirmation"
                        label="Require learner confirmation (do not pre-fill attendance)"
                        checked={historicRequestConfirmation}
                        onChange={(e) => setHistoricRequestConfirmation(e.target.checked)}
                      />
                    </Col>
                  </Row>

                  {historicCohortId && (
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Learner</th>
                          <th>Email</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getHistoricLearners().map((learner) => (
                          <tr key={learner.id}>
                            <td>{learner.displayName || 'No Name'}</td>
                            <td>{learner.email || 'N/A'}</td>
                            <td>
                              <Form.Select
                                value={historicAttendanceDraft[learner.id] || 'present'}
                                onChange={(e) => updateHistoricAttendance(learner.id, e.target.value)}
                              >
                                <option value="present">Present</option>
                                <option value="absent">Absent</option>
                                <option value="excused">Excused</option>
                                <option value="incomplete">Incomplete</option>
                              </Form.Select>
                            </td>
                          </tr>
                        ))}
                        {getHistoricLearners().length === 0 && (
                          <tr>
                            <td colSpan={3} className="text-muted text-center">
                              No learners found for this cohort.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>

              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">Custom Session Attendance (No Cohort)</h5>
                </Card.Header>
                <Card.Body>
                  {customCheckError && (
                    <Alert variant="danger" onClose={() => setCustomCheckError('')} dismissible>
                      {customCheckError}
                    </Alert>
                  )}
                  {customSessionSuccess && (
                    <Alert variant="success" onClose={() => setCustomSessionSuccess('')} dismissible>
                      {customSessionSuccess}
                    </Alert>
                  )}
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Session Title</Form.Label>
                        <Form.Control
                          type="text"
                          value={customSessionTitle}
                          onChange={(e) => setCustomSessionTitle(e.target.value)}
                          placeholder="e.g. Extra Support Session"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={customSessionDate}
                          onChange={(e) => setCustomSessionDate(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Time</Form.Label>
                        <Form.Control
                          type="time"
                          value={customSessionTime}
                          onChange={(e) => setCustomSessionTime(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label>Session Notes</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={historicSessionNotes}
                          onChange={(e) => setHistoricSessionNotes(e.target.value)}
                          placeholder="Add notes for this historic session"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label>Expected Learner Emails</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={customSessionExpectedEmails}
                          onChange={(e) => setCustomSessionExpectedEmails(e.target.value)}
                          placeholder="Add one email per line or separate with commas."
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Upload Teams CSV</Form.Label>
                        <Form.Control type="file" accept=".csv" onChange={handleCustomTeamsCsvUpload} />
                        <Form.Text className="text-muted">
                          CSV should include name or email columns.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={4}>
                      <Button
                        variant="primary"
                        onClick={runCustomSessionCheck}
                        disabled={customCheckLoading}
                      >
                        {customCheckLoading ? 'Checking...' : 'Run Check'}
                      </Button>
                    </Col>
                    <Col md={8} className="d-flex justify-content-end gap-2">
                      <Button
                        variant="outline-secondary"
                        onClick={exportCustomSessionPdf}
                        disabled={!customCheckResults}
                      >
                        Export Check PDF
                      </Button>
                      <Button
                        variant="success"
                        onClick={createCustomSession}
                        disabled={!customCheckResults || creatingCustomSession}
                      >
                        {creatingCustomSession ? 'Creating...' : 'Create Session & Trigger Confirmations'}
                      </Button>
                    </Col>
                  </Row>

                  {customCheckResults && (
                    <>
                      <Row className="mb-3">
                        <Col md={3}>
                          <Card className="text-center bg-light">
                            <Card.Body>
                              <h4 className="text-primary">{customCheckResults.totalExpected}</h4>
                              <p className="mb-0 text-muted">Expected Emails</p>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="text-center bg-success bg-opacity-10">
                            <Card.Body>
                              <h4 className="text-success">{customCheckResults.presentExpected.length}</h4>
                              <p className="mb-0 text-muted">Present (Expected)</p>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="text-center bg-danger bg-opacity-10">
                            <Card.Body>
                              <h4 className="text-danger">{customCheckResults.missingExpected.length}</h4>
                              <p className="mb-0 text-muted">Missing (Expected)</p>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="text-center bg-warning bg-opacity-10">
                            <Card.Body>
                              <h4 className="text-warning">{customCheckResults.unexpectedAttendees.length}</h4>
                              <p className="mb-0 text-muted">Unexpected</p>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>

                      {customCheckResults.missingLearnerEmails.length > 0 && (
                        <Alert variant="warning">
                          Some expected emails did not match any learner profiles:
                          <ul className="mb-0">
                            {customCheckResults.missingLearnerEmails.map((email) => (
                              <li key={email}>{email}</li>
                            ))}
                          </ul>
                        </Alert>
                      )}

                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Present (Expected)</th>
                            <th>Email</th>
                            <th>Match Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customCheckResults.presentExpected.map((row) => (
                            <tr key={row.email || row.name}>
                              <td>{row.name}</td>
                              <td>{row.email}</td>
                              <td>{row.matchType}</td>
                            </tr>
                          ))}
                          {customCheckResults.presentExpected.length === 0 && (
                            <tr>
                              <td colSpan={3} className="text-muted text-center">
                                No expected learners matched the Teams CSV.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>

                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Missing (Expected)</th>
                            <th>Email</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customCheckResults.missingExpected.map((row) => (
                            <tr key={row.email || row.name}>
                              <td>{row.name}</td>
                              <td>{row.email}</td>
                            </tr>
                          ))}
                          {customCheckResults.missingExpected.length === 0 && (
                            <tr>
                              <td colSpan={2} className="text-muted text-center">
                                No missing expected learners.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>

                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Unexpected Attendee</th>
                            <th>Email</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customCheckResults.unexpectedAttendees.map((row, index) => (
                            <tr key={`${row.email}-${index}`}>
                              <td>{row.name}</td>
                              <td>{row.email}</td>
                              <td>{row.reason}</td>
                            </tr>
                          ))}
                          {customCheckResults.unexpectedAttendees.length === 0 && (
                            <tr>
                              <td colSpan={3} className="text-muted text-center">
                                No unexpected attendees found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      </>
        )}
      </Container>

      {/* Document Review Panel Modal */}
      {selectedDocument && (
        <DocumentReviewPanel
          show={showReviewPanel}
          document={selectedDocument}
          onHide={() => setShowReviewPanel(false)}
          onReviewComplete={handleReviewComplete}
        />
      )}
    </>
  );
};

export default QADashboard;
